import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AnalogClockTimePicker from "./AnalogClockTimePicker";
import SearchableSiteSelect from "./SearchableSiteSelect";
import MinuteStepInput from "./MinuteStepInput";
import "./horizonTable.css";
import "../forms/themeCheckbox.css";
import { toast } from "react-toastify";
import XLSX from "xlsx-js-style";
import { useLocation, useNavigate } from "react-router-dom";
import EmergencyCallModal from "./EmergencyCallModal";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

const HOLIDAY_TYPE_SELECT_OPTIONS = [
  { value: "sick", label: "Sick Day" },
  { value: "holiday", label: "Holiday" },
  { value: "college", label: "College Day" },
];

function getHolidayTypeLabel(dayType) {
  const match = HOLIDAY_TYPE_SELECT_OPTIONS.find((opt) => opt.value === String(dayType || "").toLowerCase());
  return match?.label || "Working";
}

function isNonWorkingDayType(dayType) {
  const value = String(dayType || "working").toLowerCase();
  return value === "sick" || value === "holiday" || value === "college";
}

// Site belongs to the day, not just the sheet: a day marked holiday/sick/college, or
// given as a rest day, is booked to Taylors HQ rather than the customer the sheet is
// for. The backend resolves each entry's own site name, so use that — deriving it from
// the sheet here would quietly show the customer's name on a day booked to HQ.
function getRowSiteName(sheet, entry) {
  const fromEntry = typeof entry?.siteName === "string" ? entry.siteName.trim() : "";
  if (fromEntry) return fromEntry;
  if (entry && entry.siteId === null) return "—";
  return sheet?.siteId?.siteName || sheet?.siteId || "—";
}

function getWorksheetRowBackground(dayType, showOnRest) {
  if (showOnRest) {
    return { rowBg: "#fffbeb", rowHoverBg: "#fef3c7", rowBorderLeft: "4px solid #f59e0b" };
  }
  const value = String(dayType || "working").toLowerCase();
  if (value === "sick") {
    return { rowBg: "#fee2e2", rowHoverBg: "#fecaca", rowBorderLeft: "4px solid #dc2626" };
  }
  if (value === "holiday") {
    return { rowBg: "#ffedd5", rowHoverBg: "#fed7aa", rowBorderLeft: "4px solid #ea580c" };
  }
  if (value === "college") {
    return { rowBg: "#dbeafe", rowHoverBg: "#bfdbfe", rowBorderLeft: "4px solid #2563eb" };
  }
  return { rowBg: "transparent", rowHoverBg: "#f9fafb", rowBorderLeft: "4px solid transparent" };
}

function toLocalDateString(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dayKeyUTC(d) {
  if (!d) return "";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, "0")}-${String(x.getUTCDate()).padStart(2, "0")}`;
}

function formatRoundedValue(value, decimals = 2) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toFixed(decimals).replace(/\.?0+$/, "");
}

const MAX_BREAK_MINUTES = 100;
const MAX_COMMUTING_MINUTES = 999;

const ALERT_LEVEL_OPTIONS = [
  { level: 1, label: "Extremely alert" },
  { level: 2, label: "Very alert" },
  { level: 3, label: "Alert" },
  { level: 4, label: "Rather alert" },
  { level: 5, label: "Neither alert nor sleepy" },
  { level: 6, label: "Some signs of sleepiness" },
  { level: 7, label: "Sleepy, no difficulty remaining awake" },
  { level: 8, label: "Sleepy, some effort to keep awake" },
  { level: 9, label: "Extremely sleepy, fighting sleep" },
];

function getAlertLabel(level) {
  if (level == null || level === "") return "—";
  const match = ALERT_LEVEL_OPTIONS.find((o) => o.level === Number(level));
  return match ? match.label : "—";
}

function getMinuteFieldsSaveError(sheet) {
  if (!sheet) return null;
  if (sheet.holidayTypeEnabled && isNonWorkingDayType(sheet.dayType)) return null;
  const invalid = [];
  if (sheet.break_time === "" || sheet.break_time == null || Number(sheet.break_time) === 0) {
    invalid.push("Breaks (min)");
  } else if (Number(sheet.break_time) > MAX_BREAK_MINUTES) {
    return `Breaks (min) must be ${MAX_BREAK_MINUTES} or less.`;
  }
  if (sheet.travel_time === "" || sheet.travel_time == null || Number(sheet.travel_time) === 0) {
    invalid.push("Commuting Time (min)");
  } else if (Number(sheet.travel_time) > MAX_COMMUTING_MINUTES) {
    return `Commuting Time (min) must be ${MAX_COMMUTING_MINUTES} or less.`;
  }
  if (invalid.length === 0) return null;
  return `${invalid.join(" and ")} must be greater than 0.`;
}

function getCopyShiftFieldsError(sheet) {
  if (!sheet) return null;
  const missing = [];
  const zeroFields = [];
  if (sheet.break_time === "" || sheet.break_time == null) {
    missing.push("Breaks (min)");
  } else if (Number(sheet.break_time) === 0) {
    zeroFields.push("Breaks (min)");
  } else if (Number(sheet.break_time) > MAX_BREAK_MINUTES) {
    return `Breaks (min) must be ${MAX_BREAK_MINUTES} or less before copying.`;
  }
  if (sheet.travel_time === "" || sheet.travel_time == null) {
    missing.push("Commuting Time (min)");
  } else if (Number(sheet.travel_time) === 0) {
    zeroFields.push("Commuting Time (min)");
  } else if (Number(sheet.travel_time) > MAX_COMMUTING_MINUTES) {
    return `Commuting Time (min) must be ${MAX_COMMUTING_MINUTES} or less before copying.`;
  }
  if (!(sheet.onDutyTime || "").trim()) missing.push("On Duty");
  if (!(sheet.offDutyTime || "").trim()) missing.push("Off Duty");
  if (missing.length > 0) {
    return `Fill all 4 fields before copying: ${missing.join(", ")}.`;
  }
  if (zeroFields.length > 0) {
    return `${zeroFields.join(" and ")} must be greater than 0 before copying.`;
  }
  return null;
}

function FatigueWorkSheetsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSheet, setEditSheet] = useState(null);
  // Display calculated numbers with up to 2 decimal places (e.g. 7.642857... -> 7.64).
  const round2 = (v) => {
    if (v === "" || v == null) return "";
    const n = Number(v);
    return Number.isNaN(n) ? v : Math.round(n * 100) / 100;
  };
  const [showExceedanceModal, setShowExceedanceModal] = useState(false);
  const [selectedExceedanceRow, setSelectedExceedanceRow] = useState(null);
  const [appUsers, setAppUsers] = useState([]);
  const [emergencyModalOpen, setEmergencyModalOpen] = useState(false);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [copyFieldsToOtherEntries, setCopyFieldsToOtherEntries] = useState(false);
  const [restHighlight, setRestHighlight] = useState(null);
  
  const [selectedUserId, setSelectedUserId] = useState("");
  const [showWorkSheetsView, setShowWorkSheetsView] = useState(false);
  const [workStatusFilter, setWorkStatusFilter] = useState("");
  const [workerDropdownOpen, setWorkerDropdownOpen] = useState(false);
  const [workerSearch, setWorkerSearch] = useState("");
  const workerDropdownRef = useRef(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [dateRangeTab, setDateRangeTab] = useState("range");
  const dateRangeRef = useRef(null);

  // FRI Schedule modal (HSE Excel-style table)
  const [showFriModal, setShowFriModal] = useState(false);
  const [friData, setFriData] = useState(null);
  const [friLoading, setFriLoading] = useState(false);
  const [friSelectedWorkerIndex, setFriSelectedWorkerIndex] = useState(0);

  function isDateWithinSheet(sheet, dateObj) {
    if (!sheet || !dateObj) return false;
    const d = new Date(dateObj);
    d.setUTCHours(0, 0, 0, 0);
    const from = new Date(sheet.dateFrom);
    from.setUTCHours(0, 0, 0, 0);
    const toRaw = sheet.dateTo ? new Date(sheet.dateTo) : new Date(from);
    const to = new Date(toRaw);
    to.setUTCHours(23, 59, 59, 999);
    return d.getTime() >= from.getTime() && d.getTime() <= to.getTime();
  }

  function getAssignedWorkerIdsForSheet(sheet) {
    const ids = new Set();
    if (Array.isArray(sheet?.workerIds)) {
      sheet.workerIds.forEach((w) => ids.add(String(typeof w === "object" ? w?._id : w)));
    }
    if (Array.isArray(sheet?.entries)) {
      sheet.entries.forEach((e) => {
        const wid = e?.workerId && typeof e.workerId === "object" ? e.workerId?._id : e?.workerId;
        if (wid) ids.add(String(wid));
      });
    }
    return ids;
  }

  function getBusyWorkerIdsForDate(dateObj) {
    const busy = new Set();
    const targetDay = dayKeyUTC(dateObj);
    (Array.isArray(sheets) ? sheets : []).forEach((s) => {
      if (!s || (s.status || "active") !== "active") return;
      if (!isDateWithinSheet(s, dateObj)) return;
      if (Array.isArray(s.workerIds)) {
        s.workerIds.forEach((w) => {
          const wid = typeof w === "object" ? w?._id : w;
          if (wid) busy.add(String(wid));
        });
      }
      if (Array.isArray(s.entries)) {
        s.entries.forEach((e) => {
          if (!e || dayKeyUTC(e.date) !== targetDay) return;
          const wid = e?.workerId && typeof e.workerId === "object" ? e.workerId?._id : e?.workerId;
          if (wid) busy.add(String(wid));
        });
      }
    });
    return busy;
  }

  const availableCoverWorkers = (() => {
    if (!editSheet) return [];
    const needsCoverList =
      (editSheet.entryRole === "rest" && editSheet.isOnRest) ||
      (editSheet.entryRole === "normal" && editSheet.isOnRest);
    if (!needsCoverList) return [];
    const dateObj = editSheet.editDate ? new Date(editSheet.editDate) : null;
    if (!dateObj || isNaN(dateObj.getTime())) return appUsers;
    const resting = String(editSheet.restingWorkerId || "");
    const currentCover = String(editSheet.coverWorkerId || "");
    const assignedOnThatDate = getBusyWorkerIdsForDate(dateObj);
    return (Array.isArray(appUsers) ? appUsers : []).filter((u) => {
      const uid = String(u?._id);
      if (resting && uid === resting) return false;
      if (currentCover && uid === currentCover) return true;
      return !assignedOnThatDate.has(uid);
    });
  })();
  const availableWorkersForCoverEntryEdit = (() => {
    if (!editSheet || editSheet.entryRole !== "cover") return [];
    const dateObj = editSheet.editDate ? new Date(editSheet.editDate) : null;
    if (!dateObj || isNaN(dateObj.getTime())) return appUsers;
    const resting = String(editSheet.restingWorkerId || "");
    const currentCover = String(editSheet.coverWorkerId || "");
    const assignedOnThatDate = getBusyWorkerIdsForDate(dateObj);
    return (Array.isArray(appUsers) ? appUsers : []).filter((u) => {
      const uid = String(u?._id);
      if (resting && uid === resting) return false;
      if (currentCover && uid === currentCover) return true;
      return !assignedOnThatDate.has(uid);
    });
  })();

  useEffect(() => {
    fetchSheets();
    fetchSites();
    fetchAppUsers();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const workerId = params.get("workerId");
    if (!workerId) {
      setShowWorkSheetsView(false);
      setSelectedUserId("");
      setWorkerSearch("");
      return;
    }
    setShowWorkSheetsView(true);
    setSelectedUserId(workerId);
    const worker = appUsers.find((u) => String(u._id) === String(workerId));
    if (worker) {
      const name = `${worker.firstName || ""} ${worker.lastName || ""}`.trim();
      setWorkerSearch(name || worker.email || "");
    } else {
      setWorkerSearch("");
    }
  }, [location.search, appUsers]);

  useEffect(() => {
    if (!workerDropdownOpen) return;
    function handleClickOutside(e) {
      if (workerDropdownRef.current && !workerDropdownRef.current.contains(e.target)) setWorkerDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [workerDropdownOpen]);

  useEffect(() => {
    if (!dateRangeOpen) return;
    function handleClickOutside(e) {
      if (dateRangeRef.current && !dateRangeRef.current.contains(e.target)) setDateRangeOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dateRangeOpen]);

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setAppUsers(data.success && Array.isArray(data.data) ? data.data : []);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchSheets() {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/fatigue-work-sheets", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) setError("Authentication required. Please login again.");
        else setError(`Failed to load work sheets. Server responded with ${res.status}.`);
        setSheets([]);
        setLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setSheets(data.data);
        setError(null);
      } else {
        setSheets([]);
        setError(data.message || "Failed to load work sheets.");
      }
    } catch (err) {
      setSheets([]);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  async function fetchSites() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sites", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) setSites(data.data);
      }
    } catch (e) {
      console.error(e);
    }
  }

  function toDateInputValue(d) {
    if (!d) return "";
    const date = new Date(d);
    return isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
  }

  const openEditModal = (row) => {
    const s = row.sheet;
    const entry = row.entry || {};
    const siteId = typeof s.siteId === "object" ? s.siteId?._id : s.siteId;
    const sheetWorkerIdsRaw = Array.isArray(s.workerIds)
      ? s.workerIds.map((w) => (typeof w === "object" ? w._id : w)).filter(Boolean)
      : [];
    const entryWorkerId =
      entry.workerId && typeof entry.workerId === "object" ? entry.workerId?._id : entry.workerId || null;
    const normalizeId = (w) => {
      if (!w) return "";
      if (typeof w === "object" && w._id) return String(w._id);
      return String(w);
    };
    const primaryId = getPrimaryWorkerId(s);
    const rowWorkerId = row?.__workerIdForFilter ? String(row.__workerIdForFilter) : "";
    const dk = dayKeyUTC(row.date);
    const sameDay = (Array.isArray(s.entries) ? s.entries : []).filter((e) => e && dayKeyUTC(e.date) === dk);
    const currentEntryWorkerId = normalizeId(entry.workerId) || rowWorkerId || "";
    const myRestEntry = sameDay.find(
      (e) =>
        e &&
        e.isOnRest === true &&
        String(normalizeId(e.workerId)) === String(currentEntryWorkerId)
    );
    const isOnRest = Boolean(
      myRestEntry && entry?._id && String(myRestEntry._id) === String(entry._id)
    );
    const restEntry = sameDay.find((e) => e.isOnRest === true);
    const coverWorkerIdFromRest = restEntry?.coverWorkerId
      ? normalizeId(restEntry.coverWorkerId)
      : "";
    let isCoverAssigneeRow = Boolean(
      restEntry &&
        coverWorkerIdFromRest &&
        !isOnRest &&
        String(currentEntryWorkerId) === String(coverWorkerIdFromRest)
    );
    if (!isCoverAssigneeRow && restEntry && !isOnRest) {
      const nonRestOtherThanResting = sameDay.filter(
        (e) =>
          e &&
          e.isOnRest === false &&
          String(normalizeId(e.workerId)) !== String(normalizeId(restEntry.workerId))
      );
      if (
        nonRestOtherThanResting.length === 1 &&
        String(normalizeId(nonRestOtherThanResting[0].workerId)) === String(currentEntryWorkerId)
      ) {
        isCoverAssigneeRow = true;
      }
    }
    const onRestLocked = isOnRest;
    const isReplicaCoverRow = row?.__onRestAssignedReplica === true;
    let entryRole = "normal";
    if (isReplicaCoverRow) entryRole = "cover";
    else if (isOnRest) entryRole = "rest";
    let restingWorkerId = "";
    if (entryRole === "rest") {
      restingWorkerId = normalizeId(entry.workerId) || primaryId || "";
    } else if (entryRole === "cover") {
      restingWorkerId = normalizeId(restEntry?.workerId) || "";
    } else {
      restingWorkerId = normalizeId(entryWorkerId) || rowWorkerId || primaryId || "";
    }
    let coverWorkerId = "";
    if (entryRole === "rest") {
      const coverEnt = coverWorkerIdFromRest
        ? sameDay.find(
            (e) =>
              e &&
              e.isOnRest === false &&
              String(normalizeId(e.workerId)) === String(coverWorkerIdFromRest)
          )
        : sameDay.find((e) => e.isOnRest === false && String(e._id) !== String(entry._id));
      coverWorkerId = normalizeId(coverEnt?.workerId) || "";
    } else if (entryRole === "cover") {
      coverWorkerId = normalizeId(entry.workerId) || "";
    }
    const workerIds =
      entryRole === "cover"
        ? coverWorkerId
          ? [coverWorkerId]
          : []
        : entryWorkerId
          ? [normalizeId(entryWorkerId)]
          : rowWorkerId
            ? [rowWorkerId]
            : sheetWorkerIdsRaw.length
              ? [String(sheetWorkerIdsRaw[0])]
              : [];
    setEditSheet({
      _id: s._id,
      siteId: siteId || "",
      workerIds,
      isOnRest,
      onRestLocked,
      isCoverAssigneeRow,
      entryRole,
      restingWorkerId,
      coverWorkerId,
      name: s.name || "",
      dateFrom: toDateInputValue(s.dateFrom),
      dateTo: toDateInputValue(s.dateTo),
      status: s.status === "active" || s.status === "inactive" ? s.status : "active",
      notes: s.notes || "",
      workload: entry.workload ?? s.workload ?? 0,
      attention: entry.attention ?? s.attention ?? 0,
      break_frequency: entry.break_frequency ?? s.break_frequency ?? 180,
      break_average_length: entry.break_average_length ?? s.break_average_length ?? 15,
      continuous_work_length: entry.continuous_work_length ?? s.continuous_work_length ?? 360,
      break_after_continuous_work: entry.break_after_continuous_work ?? s.break_after_continuous_work ?? 30,
      entryId: entry._id || null,
      editDate: row.date,
      date: toDateInputValue(row.date),
      work_status: entry.work_status || "",
      onDutyTime: entry.onDutyTime ?? entry.on_duty_time ?? "",
      offDutyTime: entry.offDutyTime ?? entry.off_duty_time ?? "",
      break_time: entry.break_time ?? entry.breakTime ?? 0,
      travel_time: entry.travel_time ?? 0,
      dutyLengthMinutes: entry.dutyLengthMinutes ?? "",
      avgDutyPerDay: entry.avgDutyPerDay ?? "",
      cumulative: entry.cumulative ?? "",
      dutyTiming: entry.dutyTiming ?? "",
      jobTypeBreaksComponent: entry.jobTypeBreaksComponent ?? "",
      riskIndex: entry.riskIndex ?? "",
      fatigueIndex: entry.fatigueIndex ?? "",
      alert: entry.alert ?? "",
      dayType: isNonWorkingDayType(entry.dayType) ? entry.dayType : "",
      holidayTypeEnabled: isNonWorkingDayType(entry.dayType),
      holidayTypeReason: entry.holidayTypeReason || "",
      // Multi-site visits (billing add-on; independent of fatigue fields above)
      visits: Array.isArray(entry.visits)
        ? entry.visits.map((v) => ({
            _id: v._id,
            siteId: typeof v.siteId === "object" ? v.siteId?._id || "" : v.siteId || "",
            travelTime: v.travelTime ?? 15,
            timeOn: v.timeOn || "",
            timeOff: v.timeOff || "",
            status: v.status || "scheduled",
          }))
        : [],
    });
    setCopyFieldsToOtherEntries(false);
    setShowEditModal(true);
  };

  const openOnCallPage = (row) => {
    if (!row?.entry?._id) return;
    const s = row.sheet;
    const workerNameFromEntry = getEntryWorkerName(row.entry);
    const workerNameForSite =
      workerNameFromEntry !== "—" ? workerNameFromEntry : getWorkerNameById(getPrimaryWorkerId(s));
    navigate(`/admin/fatigue/work-sheets/on-call/${row.entry._id}`, {
      state: {
        sheetId: s._id,
        siteName: getRowSiteName(s, row.entry),
        date: row.date,
        workerName: workerNameForSite,
        onCallEntries: row.entry.onCallEntries || [],
        onCallCount: row.entry.onCallCount || 0,
        returnSearch: location.search || "",
      },
    });
  };

  const isEditSheetShiftFieldsLocked = (sheet) => {
    if (!sheet) return false;
    const hasRisk = sheet.riskIndex !== "" && sheet.riskIndex != null;
    const hasFatigue = sheet.fatigueIndex !== "" && sheet.fatigueIndex != null;
    return sheet.work_status === "off_duty" || hasRisk || hasFatigue;
  };

  const getEditSheetWorkerId = (sheet) => {
    if (!sheet) return null;
    if (sheet.entryRole === "cover") {
      return sheet.coverWorkerId || (Array.isArray(sheet.workerIds) ? sheet.workerIds[0] : null);
    }
    return sheet.restingWorkerId || (Array.isArray(sheet.workerIds) ? sheet.workerIds[0] : null);
  };

  const isEntryShiftFieldsLocked = (entry) => {
    if (!entry) return false;
    const hasRisk = entry.riskIndex !== "" && entry.riskIndex != null;
    const hasFatigue = entry.fatigueIndex !== "" && entry.fatigueIndex != null;
    return entry.work_status === "off_duty" || hasRisk || hasFatigue;
  };

  const getCopyTargetCount = (sourceEntryId, siteId, workerId) => {
    if (!sourceEntryId || !siteId || !workerId) return 0;
    let count = 0;
    (Array.isArray(sheets) ? sheets : []).forEach((sheet) => {
      const sheetSiteId = typeof sheet.siteId === "object" ? sheet.siteId?._id : sheet.siteId;
      if (String(sheetSiteId) !== String(siteId)) return;
      (Array.isArray(sheet.entries) ? sheet.entries : []).forEach((entry) => {
        if (!entry || entry.isOnRest) return;
        const entryWorkerId = getEntryWorkerId(entry);
        if (String(entryWorkerId) !== String(workerId)) return;
        if (String(entry._id) === String(sourceEntryId)) return;
        if (isEntryShiftFieldsLocked(entry)) return;
        count += 1;
      });
    });
    return count;
  };

  const applyCopyShiftFields = async ({
    sourceEntryId,
    siteId,
    workerId,
    break_time,
    onDutyTime,
    offDutyTime,
    travel_time,
    token,
  }) => {
    const res = await fetch(getBaseUrl() + "/api/shift-entry-copy-fields", {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        sourceEntryId,
        siteId,
        workerId,
        break_time,
        onDutyTime,
        offDutyTime,
        travel_time,
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || "Failed to copy fields");
    }
    return data;
  };

  const handleEditSave = async () => {
    if (!editSheet || !editSheet._id || !editSheet.entryId) {
      toast.error("Cannot save: entry not found");
      return;
    }
    const isCoverEntry = editSheet.entryRole === "cover";
    if (isCoverEntry && !editSheet.coverWorkerId && !(Array.isArray(editSheet.workerIds) && editSheet.workerIds[0])) {
      toast.error("Select a cover worker.");
      return;
    }
    if (!isCoverEntry && editSheet.isOnRest) {
      if (!editSheet.restingWorkerId) {
        toast.error("Worker on rest could not be determined. Ensure the work sheet has an assigned worker.");
        return;
      }
      if (!editSheet.coverWorkerId || String(editSheet.coverWorkerId) === String(editSheet.restingWorkerId)) {
        toast.error("When marking On Rest, assign a different worker to cover this date.");
        return;
      }
    }
    if (!isCoverEntry && editSheet.holidayTypeEnabled) {
      if (!isNonWorkingDayType(editSheet.dayType)) {
        toast.error("Please select Holiday Type");
        return;
      }
      if (editSheet.dayType === "sick" && !String(editSheet.holidayTypeReason || "").trim()) {
        toast.error("Please enter reason for Sick Day");
        return;
      }
      if (editSheet.dayType === "holiday" && !String(editSheet.holidayTypeReason || "").trim()) {
        toast.error("Please enter reason for Holiday");
        return;
      }
    }
    {
      // Site Visits is checked but no visit added → block save.
      const siteVisitsDisabled = Boolean(editSheet.holidayTypeEnabled) || Boolean(editSheet.isOnRest) || editSheet.work_status === "off_duty";
      const siteVisitsOn = !siteVisitsDisabled && (editSheet.siteVisitsEnabled ?? ((editSheet.visits || []).length > 0));
      if (siteVisitsOn && (editSheet.visits || []).filter((v) => v.siteId).length === 0) {
        toast.error("Please add at least one site visit, or uncheck Site Visits.");
        return;
      }
      // Site On and Site Off are required for every added site visit.
      if (siteVisitsOn) {
        for (const v of (editSheet.visits || []).filter((x) => x.siteId)) {
          if (!String(v.timeOn || "").trim() || !String(v.timeOff || "").trim()) {
            toast.error("Please fill Site On and Site Off for every site visit.");
            return;
          }
        }
      }
      // Site On / Site Off must be within the On Duty – Off Duty window.
      if (siteVisitsOn) {
        const onDuty = String(editSheet.onDutyTime || "").trim();
        const offDuty = String(editSheet.offDutyTime || "").trim();
        const toMin = (t) => {
          const m = String(t || "").trim().match(/^(\d{1,2}):(\d{2})/);
          return m ? Number(m[1]) * 60 + Number(m[2]) : null;
        };
        const onM = toMin(onDuty);
        const offM = toMin(offDuty);
        if (onM != null && offM != null) {
          const inRange = (t) => {
            const x = toMin(t);
            if (x == null) return true; // empty/invalid time is not range-checked here
            return offM >= onM ? x >= onM && x <= offM : x >= onM || x <= offM; // handles overnight shift
          };
          const rangeMsg = `Site On / Site Off must be between On Duty (${onDuty}) and Off Duty (${offDuty}).`;
          for (const v of editSheet.visits || []) {
            if (!v.siteId) continue;
            if (!inRange(v.timeOn) || !inRange(v.timeOff)) {
              toast.error(rangeMsg);
              return;
            }
          }
          // Visits must be continuous: each visit must start EXACTLY at the previous
          // visit's Site Off (no gap) — minutes normalized from On Duty, overnight-safe.
          const norm = (t) => {
            const x = toMin(t);
            if (x == null) return null;
            let rel = x - onM;
            if (rel < 0) rel += 24 * 60;
            return rel;
          };
          let prevOff = null;
          let prevOffLabel = null;
          for (const v of (editSheet.visits || []).filter((x) => x.siteId)) {
            const sOn = norm(v.timeOn);
            const sOff = norm(v.timeOff);
            if (sOn != null && sOff != null && sOff < sOn) {
              toast.error("Each visit's Site Off must be after its Site On.");
              return;
            }
            if (prevOff != null && sOn != null && sOn <= prevOff) {
              toast.error(`New visit must start after ${prevOffLabel} (previous Site Off). Change previous visit's Site Off or this visit's Site On.`);
              return;
            }
            if (sOff != null) { prevOff = sOff; prevOffLabel = v.timeOff; }
            else if (sOn != null) { prevOff = sOn; prevOffLabel = v.timeOn; }
          }
        }
      }
    }
    const minuteFieldsError = getMinuteFieldsSaveError(editSheet);
    if (minuteFieldsError) {
      toast.error(minuteFieldsError);
      return;
    }
    if (copyFieldsToOtherEntries) {
      const copyFieldsError = getCopyShiftFieldsError(editSheet);
      if (copyFieldsError) {
        toast.error(copyFieldsError);
        return;
      }
    }
    setEditModalLoading(true);
    try {
      const token = localStorage.getItem("token");
      const readJsonSafe = async (res) => {
        try {
          return await res.json();
        } catch {
          try {
            const t = await res.text();
            return t ? { message: t } : {};
          } catch {
            return {};
          }
        }
      };
      const res = await fetch(getBaseUrl() + "/api/fatigue-work-sheets/" + editSheet._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: (editSheet.name || "").trim() || undefined,
          notes: (editSheet.notes || "").trim() || undefined,
        }),
      });
      const data = await readJsonSafe(res);
      if (!res.ok || !data.success) {
        toast.error(data.message || "Failed to update work sheet");
        setEditModalLoading(false);
        return;
      }
      const entryRes = await fetch(getBaseUrl() + "/api/shift-entry-update-data", {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            id: editSheet.entryId,
            worksheet_Id: editSheet._id,
            isOnRest: isCoverEntry ? false : Boolean(editSheet.isOnRest),
            workerId: isCoverEntry
              ? (Array.isArray(editSheet.workerIds) && editSheet.workerIds[0] ? editSheet.workerIds[0] : editSheet.coverWorkerId || null)
              : editSheet.isOnRest
                ? editSheet.restingWorkerId || null
                : ((Array.isArray(editSheet.workerIds) && editSheet.workerIds[0]) ? editSheet.workerIds[0] : null),
            coverWorkerId:
              !isCoverEntry && editSheet.isOnRest && editSheet.coverWorkerId ? editSheet.coverWorkerId : undefined,
            onDutyTime: (editSheet.onDutyTime || "").trim() || null,
            offDutyTime: (editSheet.offDutyTime || "").trim() || null,
            break_time: editSheet.break_time !== "" && editSheet.break_time != null ? Number(editSheet.break_time) : 0,
            travel_time: editSheet.travel_time !== "" && editSheet.travel_time != null ? Number(editSheet.travel_time) : 0,
            workload: editSheet.workload === "" || editSheet.workload == null ? 0 : Number(editSheet.workload),
            attention: editSheet.attention === "" || editSheet.attention == null ? 0 : Number(editSheet.attention),
            break_frequency:
              editSheet.break_frequency === "" || editSheet.break_frequency == null ? 180 : Number(editSheet.break_frequency),
            break_average_length:
              editSheet.break_average_length === "" || editSheet.break_average_length == null ? 15 : Number(editSheet.break_average_length),
            continuous_work_length:
              editSheet.continuous_work_length === "" || editSheet.continuous_work_length == null ? 360 : Number(editSheet.continuous_work_length),
            break_after_continuous_work:
              editSheet.break_after_continuous_work === "" || editSheet.break_after_continuous_work == null
                ? 30
                : Number(editSheet.break_after_continuous_work),
            dutyLengthMinutes: editSheet.dutyLengthMinutes !== "" && editSheet.dutyLengthMinutes != null ? Number(editSheet.dutyLengthMinutes) : undefined,
            avgDutyPerDay: editSheet.avgDutyPerDay !== "" && editSheet.avgDutyPerDay != null ? Number(editSheet.avgDutyPerDay) : undefined,
            cumulative: editSheet.cumulative !== "" && editSheet.cumulative != null ? Number(editSheet.cumulative) : undefined,
            dutyTiming: editSheet.dutyTiming !== "" && editSheet.dutyTiming != null ? Number(editSheet.dutyTiming) : undefined,
            jobTypeBreaksComponent: editSheet.jobTypeBreaksComponent !== "" && editSheet.jobTypeBreaksComponent != null ? Number(editSheet.jobTypeBreaksComponent) : undefined,
            riskIndex: editSheet.riskIndex !== "" && editSheet.riskIndex != null ? Number(editSheet.riskIndex) : undefined,
            fatigueIndex: editSheet.fatigueIndex !== "" && editSheet.fatigueIndex != null ? Number(editSheet.fatigueIndex) : undefined,
            isManagerActionTaken: true,
            adminEdit: true,
            dayType: editSheet.isOnRest
              ? "working"
              : editSheet.holidayTypeEnabled && isNonWorkingDayType(editSheet.dayType)
                ? editSheet.dayType
                : "working",
            holidayTypeReason:
              editSheet.isOnRest || !editSheet.holidayTypeEnabled
                ? null
                : editSheet.dayType === "sick" || editSheet.dayType === "holiday"
                  ? String(editSheet.holidayTypeReason || "").trim()
                  : null,
          }),
        });
      const entryData = await readJsonSafe(entryRes);
      if (!entryRes.ok || !entryData.success) {
        toast.error(entryData.message || "Failed to update");
        setEditModalLoading(false);
        return;
      }
      // Save site visits (billing add-on; isolated from fatigue calc). Non-fatal.
      if (editSheet.entryId) {
        const visitsPayload = (editSheet.visits || [])
          .filter((v) => v.siteId)
          .map((v) => ({
            ...(v._id ? { _id: v._id } : {}),
            siteId: v.siteId,
            travelTime: v.travelTime === "" || v.travelTime == null ? 15 : Number(v.travelTime),
            timeOn: (v.timeOn || "").trim() || null,
            timeOff: (v.timeOff || "").trim() || null,
            status: v.status || "scheduled",
          }));
        try {
          await fetch(getBaseUrl() + "/api/fatigue-work-sheets/entry/" + editSheet.entryId + "/visits", {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ visits: visitsPayload }),
          });
        } catch (visitsErr) {
          console.error("Failed to save site visits:", visitsErr);
        }
      }
      if (copyFieldsToOtherEntries) {
        const workerId = getEditSheetWorkerId(editSheet);
        const targetCount = getCopyTargetCount(editSheet.entryId, editSheet.siteId, workerId);
        if (!workerId) {
          toast.error("Cannot copy: worker not found");
          setEditModalLoading(false);
          return;
        }
        if (editSheet.isOnRest) {
          toast.error("Cannot copy from On Rest entry");
          setEditModalLoading(false);
          return;
        }
        if (targetCount === 0) {
          toast.info("Saved, but no other entries to copy to for this site and worker.");
        } else {
          try {
            const copyData = await applyCopyShiftFields({
              sourceEntryId: editSheet.entryId,
              siteId: editSheet.siteId,
              workerId,
              break_time: editSheet.break_time,
              onDutyTime: editSheet.onDutyTime,
              offDutyTime: editSheet.offDutyTime,
              travel_time: editSheet.travel_time,
              token,
            });
            toast.success(copyData.message || `Saved and copied to ${targetCount} ${targetCount === 1 ? "entry" : "entries"}.`);
          } catch (copyErr) {
            toast.error(copyErr?.message || "Saved, but failed to copy fields to other entries.");
            setEditModalLoading(false);
            fetchSheets();
            return;
          }
        }
      } else {
        toast.success("Work sheet updated.");
      }
        {
          const selectedWorkerId = editSheet.isOnRest ? editSheet.coverWorkerId || null : null;
          const worker = selectedWorkerId ? appUsers.find((u) => u._id === selectedWorkerId) : null;
          const workerName = worker ? `${worker.firstName || ""} ${worker.lastName || ""}`.trim() || worker.email : "";
          const site = sites.find((st) => st._id === editSheet.siteId);
          const siteName = site?.siteName || "";
          setRestHighlight(
            selectedWorkerId
              ? { entryId: editSheet.entryId, workerId: selectedWorkerId, workerName, siteName }
              : null
          );
        }
        fetchSheets();
        setShowEditModal(false);
        setEditSheet(null);
        setCopyFieldsToOtherEntries(false);
    } catch {
      toast.error("Failed to update work sheet");
    } finally {
      setEditModalLoading(false);
    }
  };

  const openExceedanceModal = (row) => {
    if (!row || !row.entry || row.entry.isExceedance !== true) return;
    setSelectedExceedanceRow({
      siteName: getRowSiteName(row.sheet, row.entry),
      date: row.date || null,
      onDuty: row.entry?.onDutyTime || "—",
      offDuty: row.entry?.offDutyTime || "—",
      formSubmitted: row.entry?.isFormSubmitted === true ? "Yes" : "No",
      triggeredRules:
        row.entry?.triggeredRules != null && String(row.entry.triggeredRules).trim() !== ""
          ? String(row.entry.triggeredRules)
          : "No rules recorded",
    });
    setShowExceedanceModal(true);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    if (typeof d === "string") {
      const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const year = Number(m[1]);
        const month = Number(m[2]);
        const day = Number(m[3]);
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          return `${String(day).padStart(2, "0")} ${monthNames[month - 1]} ${year}`;
        }
      }
    }
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return "—";
    const day = date.getUTCDate();
    const month = date.getUTCMonth();
    const year = date.getUTCFullYear();
    return `${String(day).padStart(2, "0")} ${monthNames[month]} ${year}`;
  };

  // Show only time from value (e.g. "2026-02-26T15:56:56.149904" → "15:56" or "15:56:56")
  const formatTimeOnly = (val) => {
    if (val == null || val === "") return "—";
    const s = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}T\d/.test(s)) {
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) {
        const h = d.getHours();
        const m = d.getMinutes();
        const sec = d.getSeconds();
        return sec ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      }
    }
    return s || "—";
  };

  // Read shift times: prefer entry (per-date) when present, else sheet (legacy)
  const getSheetBreakTime = (s, entry) => {
    const src = entry || s;
    const v = src?.break_time ?? src?.breakTime;
    if (v == null || v === "") return "—";
    const num = Number(v);
    if (Number.isNaN(num) || num === 0) return "—";
    return `${num} min`;
  };
  const getSheetOnDuty = (s, entry) => formatTimeOnly(entry?.onDutyTime ?? entry?.on_duty_time ?? s?.onDutyTime ?? s?.on_duty_time);
  const getSheetOffDuty = (s, entry) => formatTimeOnly(entry?.offDutyTime ?? entry?.off_duty_time ?? s?.offDutyTime ?? s?.off_duty_time);

  // Normalize to "HH:mm" for calculations (handles ISO datetime or "HH:mm" / "HH:mm:ss")
  const toHHmm = (val) => {
    if (val == null || val === "") return null;
    const s = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}T\d/.test(s)) {
      const d = new Date(s);
      if (!Number.isNaN(d.getTime()))
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    const parts = s.split(":");
    if (parts.length >= 2) return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
    return null;
  };

  // Format a number of minutes as "X hr Y min"
  const formatMinutesAsHoursMinutes = (value) => {
    if (value == null || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return "—";
    const mins = Math.max(0, Math.round(num));
    const hours = Math.floor(mins / 60);
    const rem = mins % 60;
    if (hours === 0) return `${rem} min`;
    if (rem === 0) return `${hours} hr`;
    return `${hours} hr ${rem} min`;
  };

  const openFriModal = async (sheetId) => {
    setShowFriModal(true);
    setFriData(null);
    setFriSelectedWorkerIndex(0);
    setFriLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/fatigue-work-sheets/" + sheetId + "/fri-schedule", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success && data.data) {
        setFriData(data.data);
      } else {
        toast.error(data.message || "Failed to load FRI schedule");
        setShowFriModal(false);
      }
    } catch {
      toast.error("Failed to load FRI schedule");
      setShowFriModal(false);
    } finally {
      setFriLoading(false);
    }
  };

  const closeFriModal = () => {
    setShowFriModal(false);
    setFriData(null);
    setFriSelectedWorkerIndex(0);
  };

  const currentFriWorker = friData?.workers?.[friSelectedWorkerIndex];
  const friRows = currentFriWorker?.rows || [];

  // 23 columns: Site, Date, Work status, Breaks, ... Alert, Updated Date, Actions
  const gridCols = "minmax(140px,1fr) minmax(95px,0.8fr) minmax(110px,0.8fr) minmax(75px,0.6fr) minmax(170px,1fr) minmax(170px,1fr) minmax(110px,0.8fr) minmax(130px,0.9fr) minmax(130px,0.9fr) minmax(140px,1fr) minmax(85px,0.6fr) minmax(85px,0.6fr) minmax(130px,0.9fr) minmax(100px,0.7fr) minmax(95px,0.6fr) minmax(110px,0.8fr) minmax(110px,0.8fr) minmax(120px,0.9fr) minmax(150px,1fr) minmax(90px,0.6fr) minmax(95px,0.7fr) minmax(150px,1fr) minmax(110px,0.8fr) minmax(95px,0.7fr)";
  const cellEllipsis = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
  const tableMinWidth = "3280px";

  const isActiveSheet = (sheet) => (sheet?.status || "active") === "active";
  const activeSheets = sheets.filter(isActiveSheet);
  const getPrimaryWorkerId = (sheet) => {
    if (!sheet || !Array.isArray(sheet.workerIds) || sheet.workerIds.length === 0) return null;
    const first = sheet.workerIds[0];
    if (typeof first === "object" && first) return first._id ? String(first._id) : null;
    return first ? String(first) : null;
  };
  const getEntryWorkerId = (entry) => {
    if (!entry || !entry.workerId) return null;
    if (typeof entry.workerId === "object" && entry.workerId) return entry.workerId._id ? String(entry.workerId._id) : null;
    return String(entry.workerId);
  };
  const getEntryWorkerName = (entry) => {
    const workerObj = entry?.workerId && typeof entry.workerId === "object" ? entry.workerId : null;
    if (workerObj) {
      const full = `${workerObj.firstName || ""} ${workerObj.lastName || ""}`.trim();
      return full || workerObj.email || "—";
    }
    const workerId = getEntryWorkerId(entry);
    if (!workerId) return "—";
    return getWorkerNameById(workerId);
  };
  const getWorkerNameById = (workerId) => {
    if (!workerId) return "—";
    const worker = appUsers.find((u) => String(u._id) === String(workerId));
    if (!worker) return "—";
    const name = `${worker.firstName || ""} ${worker.lastName || ""}`.trim();
    return name || worker.email || "—";
  };
  const getLatestEntryFromSheet = (sheet) => {
    const entries = Array.isArray(sheet?.entries) ? sheet.entries : [];
    if (!entries.length) return null;
    const sorted = entries
      .slice()
      .filter((entry) => entry && entry.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return sorted[0] || null;
  };
  const getLatestEntryForWorker = (sheet, workerId) => {
    const w = String(workerId);
    const entries = Array.isArray(sheet?.entries) ? sheet.entries : [];
    const matched = entries
      .filter((e) => e && e.date && String(getEntryWorkerId(e) || "") === w)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return matched[0] || null;
  };
  const collectWorkerIdsForSheet = (sheet) => {
    const idSet = new Set();
    (Array.isArray(sheet?.workerIds) ? sheet.workerIds : []).forEach((w) => {
      const id = typeof w === "object" && w?._id ? String(w._id) : w ? String(w) : null;
      if (id) idSet.add(id);
    });
    (Array.isArray(sheet?.entries) ? sheet.entries : []).forEach((e) => {
      const id = getEntryWorkerId(e);
      if (id) idSet.add(id);
    });
    return idSet;
  };
  const getWorkStatusLabel = (workStatus, showOnRest, dayType) => {
    if (showOnRest) return "On Rest";
    if (isNonWorkingDayType(dayType)) {
      return getHolidayTypeLabel(dayType);
    }
    if (workStatus === "on_duty") return "Shift Started";
    if (workStatus === "off_duty") return "Completed";
    if (workStatus === "break") return "Break";
    if (workStatus === "resume") return "Resume";
    if (workStatus === "sleep") return "Sleep";
    return "—";
  };

  const getWorkloadLabel = (v) => {
    const n = v == null ? null : Number(v);
    if (n === 0) return "Extremely demanding, no spare capacity";
    if (n === 1) return "Moderately demanding, little spare capacity";
    if (n === 2) return "Moderately undemanding, some spare capacity";
    if (n === 3) return "Extremely undemanding, lots of spare capacity";
    return "—";
  };

  const getAttentionLabel = (v) => {
    const n = v == null ? null : Number(v);
    if (n === 0) return "All or nearly all the time";
    if (n === 1) return "Most of the Time";
    if (n === 2) return "Some of the time";
    if (n === 3) return "Rarely or Nearly none of the time";
    return "—";
  };

  const workersSummaryRows = (() => {
    const byWorkerId = new Map();
    const upsert = (workerId, siteName, startDate, endDate, updatedDate) => {
      if (!workerId) return;
      const wid = String(workerId);
      const existing = byWorkerId.get(wid);
      const start = startDate || null;
      const end = endDate || startDate || null;
      const updated = updatedDate || end || start || null;
      if (!existing) {
        byWorkerId.set(wid, {
          id: `worker-${wid}`,
          workerId: wid,
          workerName: getWorkerNameById(wid),
          siteNames: new Set(siteName ? [String(siteName)] : []),
          startDate: start,
          endDate: end,
          updatedDate: updated,
        });
        return;
      }
      if (siteName) existing.siteNames.add(String(siteName));
      if (start && (!existing.startDate || new Date(start).getTime() < new Date(existing.startDate).getTime())) existing.startDate = start;
      if (end && (!existing.endDate || new Date(end).getTime() > new Date(existing.endDate).getTime())) existing.endDate = end;
      if (updated && (!existing.updatedDate || new Date(updated).getTime() > new Date(existing.updatedDate).getTime())) existing.updatedDate = updated;
    };
    const upsertUnassigned = (sheetId, siteName, startDate, endDate, updatedDate) => {
      const key = `unassigned-${String(sheetId)}`;
      const existing = byWorkerId.get(key);
      const start = startDate || null;
      const end = endDate || startDate || null;
      const updated = updatedDate || end || start || null;
      if (!existing) {
        byWorkerId.set(key, {
          id: key,
          workerId: null,
          workerName: "Unassigned",
          siteNames: new Set(siteName ? [String(siteName)] : []),
          startDate: start,
          endDate: end,
          updatedDate: updated,
        });
        return;
      }
      if (siteName) existing.siteNames.add(String(siteName));
      if (start && (!existing.startDate || new Date(start).getTime() < new Date(existing.startDate).getTime())) existing.startDate = start;
      if (end && (!existing.endDate || new Date(end).getTime() > new Date(existing.endDate).getTime())) existing.endDate = end;
      if (updated && (!existing.updatedDate || new Date(updated).getTime() > new Date(existing.updatedDate).getTime())) existing.updatedDate = updated;
    };

    (Array.isArray(activeSheets) ? activeSheets : []).forEach((sheet) => {
      const siteName = sheet.siteId?.siteName || sheet.siteId || "—";
      const primaryWorkerId = getPrimaryWorkerId(sheet);
      const workerIdSet = collectWorkerIdsForSheet(sheet);
      const fallbackLatest = getLatestEntryFromSheet(sheet);
      workerIdSet.forEach((wid) => {
        const workerEntries = (Array.isArray(sheet.entries) ? sheet.entries : [])
          .filter((e) => {
            if (!e?.date || e?.isOnRest === true) return false;
            const entryWorkerId = getEntryWorkerId(e);
            if (entryWorkerId) return String(entryWorkerId) === String(wid);
            return primaryWorkerId && String(primaryWorkerId) === String(wid);
          })
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const latestForWorker = workerEntries.length
          ? workerEntries[workerEntries.length - 1]
          : getLatestEntryForWorker(sheet, wid);
        const latestEntry = latestForWorker || fallbackLatest;
        const workerStartDate = workerEntries.length ? workerEntries[0].date : sheet.dateFrom;
        const workerEndDate = workerEntries.length ? workerEntries[workerEntries.length - 1].date : (sheet.dateTo || sheet.dateFrom);
        upsert(
          wid,
          siteName,
          workerStartDate,
          workerEndDate,
          latestEntry?.updatedAt || sheet.updatedAt || sheet.createdAt || sheet.dateFrom
        );
      });
      if (workerIdSet.size === 0) {
        upsertUnassigned(
          sheet._id,
          siteName,
          sheet.dateFrom,
          sheet.dateTo || sheet.dateFrom,
          fallbackLatest?.updatedAt || sheet.updatedAt || sheet.createdAt || sheet.dateFrom
        );
      }

      (Array.isArray(sheet.entries) ? sheet.entries : [])
        .filter((entry) => entry?.isOnRest === true && getEntryWorkerId(entry))
        .forEach((entry) => {
          const wid = getEntryWorkerId(entry);
          upsert(
            wid,
            siteName,
            entry.date || sheet.dateFrom,
            entry.date || sheet.dateFrom,
            entry.updatedAt || entry.date || sheet.updatedAt || sheet.createdAt || sheet.dateFrom
          );
        });
    });

    return Array.from(byWorkerId.values())
      .map((row) => ({
        ...row,
        siteName: row.siteNames.size === 1 ? Array.from(row.siteNames)[0] : row.siteNames.size > 1 ? "Multiple" : "—",
      }))
      .sort((a, b) => new Date(b.updatedDate || 0).getTime() - new Date(a.updatedDate || 0).getTime());
  })();
  const filteredSheets = selectedUserId
    ? activeSheets.filter((sheet) => {
        const inSheetWorkers = Array.isArray(sheet.workerIds)
          ? sheet.workerIds.some((worker) => {
              const workerId = typeof worker === "object" ? worker._id : worker;
              return String(workerId) === String(selectedUserId);
            })
          : false;
        if (inSheetWorkers) return true;
        const inEntries = Array.isArray(sheet.entries)
          ? sheet.entries.some((entry) => String(getEntryWorkerId(entry) || "") === String(selectedUserId))
          : false;
        return inEntries;
      })
    : activeSheets;

  const workerOptions = appUsers.map((user) => ({
    id: user._id,
    name: `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "Unnamed",
  }));

  const worksheetWorkersToday = (() => {
    const todayStart = new Date();
    todayStart.setUTCHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setUTCHours(23, 59, 59, 999);
    const sheets = selectedUserId ? filteredSheets : activeSheets;
    const map = new Map();

    (Array.isArray(sheets) ? sheets : []).forEach((sheet) => {
      const siteId = String(
        typeof sheet.siteId === "object" ? sheet.siteId?._id || "" : sheet.siteId || ""
      );
      const siteName = sheet.siteId?.siteName || "—";
      const primaryWorkerId = getPrimaryWorkerId(sheet);
      const workerIdSet = collectWorkerIdsForSheet(sheet);

      workerIdSet.forEach((wid) => {
        if (selectedUserId && String(wid) !== String(selectedUserId)) return;

        const workerEntries = (Array.isArray(sheet.entries) ? sheet.entries : [])
          .filter((e) => {
            if (!e?.date || e?.isOnRest === true) return false;
            const entryWorkerId = getEntryWorkerId(e);
            if (entryWorkerId) return String(entryWorkerId) === String(wid);
            return primaryWorkerId && String(primaryWorkerId) === String(wid);
          })
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const rangeFrom = workerEntries.length ? workerEntries[0].date : sheet.dateFrom;
        const rangeTo = workerEntries.length
          ? workerEntries[workerEntries.length - 1].date
          : sheet.dateTo || sheet.dateFrom;
        const from = new Date(rangeFrom);
        from.setUTCHours(0, 0, 0, 0);
        const to = new Date(rangeTo || rangeFrom);
        to.setUTCHours(23, 59, 59, 999);
        if (from.getTime() > todayEnd.getTime() || to.getTime() < todayStart.getTime()) return;

        const key = String(wid);
        if (!map.has(key)) {
          map.set(key, {
            workerId: key,
            name: getWorkerNameById(wid),
            siteId,
            siteName,
            workSheetId: String(sheet._id),
          });
        }
      });
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  function parseTimeToMinutes(timeStr) {
    if (!timeStr) return null;
    const raw = String(timeStr).trim();
    if (/^\d{4}-\d{2}-\d{2}T\d/.test(raw)) {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return null;
      return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
    }
    const parts = raw.split(":");
    if (parts.length < 2) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parts.length >= 3 ? parseInt(parts[2], 10) : 0;
    if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
    return hours * 60 + minutes + seconds / 60;
  }

  function calculateShiftDutyLengthMinutes(onDutyTime, offDutyTime) {
    const onDutyMins = parseTimeToMinutes(onDutyTime);
    const offDutyMins = parseTimeToMinutes(offDutyTime);
    if (onDutyMins === null || offDutyMins === null) return null;
    if (offDutyMins >= onDutyMins) return Math.round(offDutyMins - onDutyMins);
    const maxShiftMins = 12 * 60;
    const overnightMins = offDutyMins - onDutyMins + 24 * 60;
    if (overnightMins > maxShiftMins && onDutyMins < 12 * 60 && offDutyMins <= 8 * 60) {
      const sameDayPmMins = offDutyMins + 12 * 60 - onDutyMins;
      if (sameDayPmMins > 0 && sameDayPmMins <= maxShiftMins && sameDayPmMins >= 4 * 60) {
        return Math.round(sameDayPmMins);
      }
    }
    return overnightMins >= 0 ? Math.round(overnightMins) : null;
  }

  function calculateOnCallDutyLengthMinutes(onDutyTime, offDutyTime) {
    const onDutyMins = parseTimeToMinutes(onDutyTime);
    const offDutyMins = parseTimeToMinutes(offDutyTime);
    if (onDutyMins === null || offDutyMins === null) return null;
    if (offDutyMins >= onDutyMins) return Math.round(offDutyMins - onDutyMins);
    return Math.round(offDutyMins - onDutyMins + 24 * 60);
  }

  function calculateDutyLength(onDutyTime, offDutyTime) {
    const totalMins = calculateShiftDutyLengthMinutes(onDutyTime, offDutyTime);
    if (totalMins == null || totalMins < 0) return "—";
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  }

  function sumOnCallDutyMinutes(onCallEntries) {
    if (!Array.isArray(onCallEntries)) return 0;
    return onCallEntries.reduce(
      (sum, oc) => sum + (calculateOnCallDutyLengthMinutes(oc?.onDuty, oc?.offDuty) || 0),
      0
    );
  }

  function displayDutyLengthCell(entryOrSheet) {
    const e = entryOrSheet;
    if (!e) return "—";
    if (isNonWorkingDayType(e.dayType)) return formatMinutesAsHoursMinutes(0);
    const onCallEntries = Array.isArray(e.onCallEntries) ? e.onCallEntries : [];
    const hasOnCall = (e.onCallCount > 0) || onCallEntries.length > 0;
    if (hasOnCall) {
      const shiftOn = e.onDutyTime ?? e.on_duty_time;
      const shiftOff = e.baseShiftOffDutyTime ?? null;
      const onCallMins = sumOnCallDutyMinutes(onCallEntries);
      let shiftMins = shiftOff ? calculateShiftDutyLengthMinutes(shiftOn, shiftOff) : null;
      if (shiftMins == null && e.dutyLengthMinutes != null && onCallMins > 0) {
        shiftMins = Math.max(0, Number(e.dutyLengthMinutes) - onCallMins);
      }
      if (shiftMins != null) {
        return formatMinutesAsHoursMinutes(shiftMins + onCallMins);
      }
      if (e.dutyLengthMinutes != null) return formatMinutesAsHoursMinutes(e.dutyLengthMinutes);
    }
    const on = e.onDutyTime ?? e.on_duty_time;
    const off = e.offDutyTime ?? e.off_duty_time;
    if (on && off) {
      const span = calculateDutyLength(on, off);
      if (span !== "—") return span;
    }
    if (e.dutyLengthMinutes != null) return formatMinutesAsHoursMinutes(e.dutyLengthMinutes);
    return "—";
  }

  const toDayKey = dayKeyUTC;

  function getSheetDateRows(sheet) {
    const from = new Date(sheet.dateFrom);
    const to = new Date(sheet.dateTo || sheet.dateFrom);
    const entries = Array.isArray(sheet.entries) ? sheet.entries : [];
    const entriesByKey = {};
    for (const e of entries) {
      if (!e || !e.date) continue;
      const key = toDayKey(e.date);
      if (!entriesByKey[key]) entriesByKey[key] = [];
      entriesByKey[key].push(e);
    }
    let current = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
    const end = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate()));
    const rows = [];
    if (current.getTime() > end.getTime()) {
      const key = toDayKey(current);
      const dayEntries = entriesByKey[key] || [];
      if (dayEntries.length === 0) rows.push({ sheet, date: new Date(current.getTime()), entry: null });
      else dayEntries.forEach((e) => rows.push({ sheet, date: new Date(current.getTime()), entry: e }));
      return rows;
    }
    while (current.getTime() <= end.getTime()) {
      const key = toDayKey(current);
      const dayEntries = entriesByKey[key] || [];
      if (dayEntries.length === 0) {
        rows.push({ sheet, date: new Date(current.getTime()), entry: null });
      } else {
        dayEntries.forEach((e) => rows.push({ sheet, date: new Date(current.getTime()), entry: e }));
      }
      current.setUTCDate(current.getUTCDate() + 1);
    }
    return rows;
  }
  let expandedRows = filteredSheets
    .flatMap((s) => getSheetDateRows(s))
    .filter((row) => row.entry != null)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const hasSiblingCoverEntry = (sheet, entry) => {
    if (!entry?.date) return false;
    const key = toDayKey(entry.date);
    return (sheet.entries || []).some(
      (e) =>
        e &&
        String(e._id) !== String(entry._id) &&
        toDayKey(e.date) === key &&
        e.isOnRest === false &&
        getEntryWorkerId(e)
    );
  };
  const expandedRowsForDisplay = expandedRows.flatMap((row) => {
    const primaryWorkerId = getPrimaryWorkerId(row.sheet);
    const assignedWorkerId = getEntryWorkerId(row.entry);
    const selectedAssignedInSheet = selectedUserId
      ? (Array.isArray(row.sheet?.workerIds) ? row.sheet.workerIds : []).some((w) => {
          const wid = typeof w === "object" ? w?._id : w;
          return String(wid || "") === String(selectedUserId);
        })
      : false;
    const baseRow = {
      ...row,
      __workerIdForFilter: assignedWorkerId || (selectedAssignedInSheet ? selectedUserId : primaryWorkerId) || null,
    };
    if (
      row.entry?.isOnRest === true &&
      primaryWorkerId &&
      assignedWorkerId &&
      String(primaryWorkerId) !== String(assignedWorkerId) &&
      !hasSiblingCoverEntry(row.sheet, row.entry)
    ) {
      const assignedRow = { ...row, __workerIdForFilter: assignedWorkerId, __onRestAssignedReplica: true };
      return [baseRow, assignedRow];
    }
    return [baseRow];
  });

  let visibleRows = expandedRowsForDisplay;
  if (selectedUserId) {
    visibleRows = visibleRows.filter((row) => String(row.__workerIdForFilter || "") === String(selectedUserId));
  }

  if (startDate || endDate) {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    if (start) start.setUTCHours(0, 0, 0, 0);
    if (end) end.setUTCHours(23, 59, 59, 999);
    visibleRows = visibleRows.filter((row) => {
      const t = row.date.getTime();
      if (start && t < start.getTime()) return false;
      if (end && t > end.getTime()) return false;
      return true;
    });
  }

  if (workStatusFilter) {
    visibleRows = visibleRows.filter((row) => (row.entry?.work_status || "") === workStatusFilter);
  }
  const displayRows = visibleRows;

  const handleExportExcel = () => {
    if (!Array.isArray(displayRows) || displayRows.length === 0) {
      toast.info("No work sheet rows to export for the selected range.");
      return;
    }
    try {
      const THEME = "8B1634"; // maroon (no #)
      const headers = [
        "Worker", "Site", "Date", "Work Status", "On Duty", "Off Duty",
        "Commuting Time", "Duty Length", "Risk Index", "Fatigue Index", "Alert", "Updated Date",
      ];

      const rowsData = displayRows.map((row) => {
        const s = row.sheet || {};
        const wn = getEntryWorkerName(row.entry);
        const worker = wn && wn !== "—" ? wn : getWorkerNameById(getPrimaryWorkerId(s));
        const commuting =
          row.entry?.travel_time != null && Number(row.entry.travel_time) > 0
            ? formatMinutesAsHoursMinutes(row.entry.travel_time)
            : "—";
        return [
          worker || "—",
          getRowSiteName(s, row.entry),
          formatDate(row.date),
          getWorkStatusLabel(row.entry?.work_status || "", row.entry?.isOnRest === true, row.entry?.dayType || "working"),
          getSheetOnDuty(s, row.entry),
          getSheetOffDuty(s, row.entry),
          commuting,
          displayDutyLengthCell(row.entry || s),
          row.entry?.riskIndex != null ? row.entry.riskIndex : "—",
          row.entry?.fatigueIndex != null ? row.entry.fatigueIndex : "—",
          getAlertLabel(row.entry?.alert),
          formatDate(s.updatedAt || s.createdAt || row.date),
        ];
      });

      const workerNameForFile = selectedUserId ? getWorkerNameById(selectedUserId) : "All Workers";
      const rangeLabel = startDate && endDate ? `${startDate} to ${endDate}` : "";
      const title = `Work Sheets — ${workerNameForFile}${rangeLabel ? `  (${rangeLabel})` : ""}`;

      const aoa = [[title], headers, ...rowsData];
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      ws["!cols"] = [15, 30, 12, 18, 10, 10, 16, 13, 11, 13, 32, 14].map((wch) => ({ wch }));
      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
      ws["!rows"] = [{ hpt: 28 }, { hpt: 22 }];

      const thin = (rgb) => ({ style: "thin", color: { rgb } });
      const allBorders = (rgb) => ({ top: thin(rgb), bottom: thin(rgb), left: thin(rgb), right: thin(rgb) });
      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let R = range.s.r; R <= range.e.r; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const ref = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[ref];
          if (!cell) continue;
          if (R === 0) {
            cell.s = {
              font: { bold: true, sz: 15, color: { rgb: THEME } },
              fill: { fgColor: { rgb: "F5DEE6" } },
              alignment: { horizontal: "left", vertical: "center" },
              border: allBorders("E6C9D4"),
            };
          } else if (R === 1) {
            cell.s = {
              font: { bold: true, sz: 12, color: { rgb: THEME } },
              fill: { fgColor: { rgb: "EFD1DB" } },
              alignment: { horizontal: "left", vertical: "center", wrapText: true },
              border: allBorders("E6C9D4"),
            };
          } else {
            const bg = (R - 2) % 2 === 0 ? "FFFFFF" : "FCF3F6";
            cell.s = {
              font: { sz: 11, color: { rgb: "2D3748" } },
              fill: { fgColor: { rgb: bg } },
              alignment: { horizontal: "left", vertical: "center" },
              border: allBorders("EADBE1"),
            };
          }
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Work Sheets");
      const safe = (v) => String(v || "export").replace(/[^a-z0-9]+/gi, "_");
      const rangePart = startDate && endDate ? `_${startDate}_to_${endDate}` : "";
      XLSX.writeFile(wb, `WorkSheets_${safe(workerNameForFile)}${rangePart}.xlsx`);
    } catch (e) {
      toast.error("Failed to export Excel.");
    }
  };

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "20px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "24px",
        marginBottom: "24px",
      }}
    >
      {!showWorkSheetsView && (
      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c", margin: "0 0 14px 0" }}>
          Workers Work Sheets
        </h2>
        <div style={{ overflowX: "auto", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(240px,1fr) minmax(250px,1fr) minmax(130px,0.9fr) minmax(130px,0.9fr) minmax(150px,1fr)",
              minWidth: "1000px",
              background: "#e8f4fc",
              padding: "12px 16px",
              borderBottom: "1px solid #e2e8f0",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", ...cellEllipsis }}>Site</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", ...cellEllipsis }}>Worker name</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", ...cellEllipsis }}>Start date</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", ...cellEllipsis }}>End date</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", ...cellEllipsis }}>Updated Date</div>
          </div>
          {workersSummaryRows.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 20px", color: "#718096" }}>No active work sheets found.</div>
          ) : (
            workersSummaryRows.map((row) => {
              return (
                <div
                  key={row.id}
                  onClick={() => {
                    if (!row.workerId) return;
                    setWorkStatusFilter("");
                    setDateRangeOpen(false);
                    const next = new URLSearchParams(location.search || "");
                    next.set("workerId", String(row.workerId));
                    navigate({ pathname: location.pathname, search: `?${next.toString()}` });
                  }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(240px,1fr) minmax(250px,1fr) minmax(130px,0.9fr) minmax(130px,0.9fr) minmax(150px,1fr)",
                    minWidth: "1000px",
                    padding: "16px",
                    borderBottom: "1px solid #f1f5f9",
                    gap: "16px",
                    alignItems: "center",
                    cursor: row.workerId ? "pointer" : "default",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!row.workerId) return;
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }} title={row.siteName}>
                    {row.siteName}
                  </div>
                  <div style={{ color: "#8b1634", fontSize: "14px", ...cellEllipsis, fontWeight: 700 }}>
                    {row.workerName}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{formatDate(row.startDate)}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{formatDate(row.endDate)}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{formatDate(row.updatedDate)}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
      )}
      {showWorkSheetsView && (
      <>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", flexBasis: "100%" }}>
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(location.search || "");
              next.delete("workerId");
              navigate({ pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : "" });
            }}
            style={{
              height: "34px",
              padding: "0 10px",
              borderRadius: "999px",
              border: "1px solid rgba(139, 22, 52, 0.25)",
              background: "rgba(139, 22, 52, 0.06)",
              fontSize: "13px",
              fontWeight: 600,
              color: "#8b1634",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              lineHeight: 1,
              boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
              transition: "background-color 0.15s ease, border-color 0.15s ease, transform 0.05s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(139, 22, 52, 0.10)";
              e.currentTarget.style.borderColor = "rgba(139, 22, 52, 0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(139, 22, 52, 0.06)";
              e.currentTarget.style.borderColor = "rgba(139, 22, 52, 0.25)";
              e.currentTarget.style.transform = "none";
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = "translateY(1px)";
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = "none";
            }}
          >
            <i className="fa fa-arrow-left" style={{ color: "#8b1634", fontSize: "12px" }} />
            <span className="d-none d-sm-inline">Back</span>
          </button>
          <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c", margin: 0 }}>
             Work Sheets
          </h2>
          {startDate && endDate && displayRows.length > 0 && (
            <button
              type="button"
              onClick={handleExportExcel}
              title="Export the selected date range to Excel"
              style={{
                marginLeft: "auto",
                padding: "8px 14px",
                borderRadius: "8px",
                border: "1px solid #8b1634",
                background: "#fff",
                fontSize: "13px",
                fontWeight: 600,
                color: "#8b1634",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
                lineHeight: 1,
                transition: "background-color 0.15s ease, color 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#8b1634";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fff";
                e.currentTarget.style.color = "#8b1634";
              }}
            >
              <i className="fa fa-file-excel" style={{ fontSize: "12px" }} />
              <span>Export Excel</span>
            </button>
          )}
        </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "nowrap" }}>
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(location.search || "");
              if (visibleRows.length > 0) {
                const times = visibleRows
                  .map((row) => (row.date instanceof Date ? row.date.getTime() : new Date(row.date).getTime()))
                  .filter((t) => Number.isFinite(t));
                if (times.length > 0) {
                  next.set("startDate", toLocalDateString(new Date(Math.min(...times))));
                  next.set("endDate", toLocalDateString(new Date(Math.max(...times))));
                }
              }
              const siteIds = new Set();
              filteredSheets.forEach((sheet) => {
                const sid = typeof sheet.siteId === "object" ? sheet.siteId?._id : sheet.siteId;
                if (sid) siteIds.add(String(sid));
              });
              if (siteIds.size === 1) {
                next.set("siteId", Array.from(siteIds)[0]);
              }
              const search = next.toString() ? `?${next.toString()}` : "";
              navigate({ pathname: "/admin/fatigue/work-sheets/standby", search });
            }}
            style={{
              height: "34px",
              padding: "0 12px",
              borderRadius: "999px",
              border: "none",
              background: "#8b1634",
              fontSize: "13px",
              fontWeight: 600,
              color: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              lineHeight: 1,
            }}
          >
            <i className="fa fa-bell" style={{ fontSize: "12px" }} />
            <span>Standby</span>
          </button>
          <button
            type="button"
            onClick={() => setEmergencyModalOpen(true)}
            style={{
              height: "34px",
              padding: "0 12px",
              borderRadius: "999px",
              border: "none",
              background: "#dc2626",
              fontSize: "13px",
              fontWeight: 600,
              color: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              lineHeight: 1,
            }}
          >
            <i className="fa fa-exclamation-triangle" style={{ fontSize: "12px" }} />
            <span>Emergency</span>
          </button>
          <div ref={dateRangeRef} style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => {
                setDateRangeOpen(!dateRangeOpen);
                setTempStartDate(startDate ? new Date(startDate) : null);
                setTempEndDate(endDate ? new Date(endDate) : null);
              }}
            style={{
                padding: "8px 14px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
                background: "#fff",
              fontSize: "14px",
                color: "#4a5568",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
            }}
            >
              <i className="fa fa-calendar" style={{ color: "#8b1634" }} />
              {startDate && endDate
                ? (() => {
                    const fmt = (s) => {
                      const [y, m, d] = s.split("-").map(Number);
                      return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
                    };
                    return `${fmt(startDate)} - ${fmt(endDate)}`;
                  })()
                : "Select date range"}
            </button>
            {dateRangeOpen && (
              <div
                className="worksheet-date-range-picker"
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  marginTop: "4px",
                  background: "#fff",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                  zIndex: 1000,
                  padding: "10px 12px",
                  width: "min(280px, 90vw)",
                  boxSizing: "border-box",
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div style={{ display: "flex", gap: "3px", marginBottom: "8px", borderBottom: "1px solid #e2e8f0", paddingBottom: "4px" }}>
                  {["Presets", "Days", "Weeks", "Months", "Range"].map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setDateRangeTab(tab.toLowerCase())}
                      style={{
                        padding: "4px 8px",
                        fontSize: "11px",
                        borderRadius: "6px",
                        border: "none",
                        background: dateRangeTab === tab.toLowerCase() ? "#8b1634" : "#f1f5f9",
                        color: dateRangeTab === tab.toLowerCase() ? "#fff" : "#64748b",
                        cursor: "pointer",
                        fontWeight: 500,
                      }}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                {dateRangeTab === "range" && (
                  <>
                    <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <label style={{ fontSize: "11px", color: "#64748b", marginBottom: "2px", display: "block" }}>Start Date</label>
                        <input
                          type="text"
                          value={tempStartDate ? tempStartDate.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            const d = new Date(v);
                            if (!isNaN(d.getTime())) setTempStartDate(d);
                          }}
                          placeholder="MM/DD/YYYY"
                          style={{ width: "100%", padding: "6px 8px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "12px", outline: "none", boxSizing: "border-box" }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <label style={{ fontSize: "11px", color: "#64748b", marginBottom: "2px", display: "block" }}>End Date</label>
                        <input
                          type="text"
                          value={tempEndDate ? tempEndDate.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            const d = new Date(v);
                            if (!isNaN(d.getTime())) setTempEndDate(d);
                          }}
                          placeholder="MM/DD/YYYY"
                          style={{ width: "100%", padding: "6px 8px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "12px", outline: "none", boxSizing: "border-box" }}
                        />
                      </div>
                    </div>
                    <div style={{ marginBottom: "8px", display: "flex", justifyContent: "center" }}>
                      <DatePicker
                        selected={tempStartDate}
                        onChange={(dates) => {
                          const [start, end] = dates;
                          setTempStartDate(start);
                          setTempEndDate(end);
                        }}
                        selectsRange
                        startDate={tempStartDate}
                        endDate={tempEndDate}
                        inline
                        monthsShown={1}
                      />
                    </div>
                  </>
                )}
                {dateRangeTab === "presets" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "8px" }}>
                    {[
                      { label: "Today", getRange: () => { const d = new Date(); return [d, d]; } },
                      { label: "Yesterday", getRange: () => { const d = new Date(); d.setDate(d.getDate() - 1); return [d, d]; } },
                      { label: "Last 7 days", getRange: () => { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 6); return [start, end]; } },
                      { label: "Last 30 days", getRange: () => { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 29); return [start, end]; } },
                      { label: "This month", getRange: () => { const d = new Date(); const start = new Date(d.getFullYear(), d.getMonth(), 1); const end = new Date(d.getFullYear(), d.getMonth() + 1, 0); return [start, end]; } },
                      { label: "Last month", getRange: () => { const d = new Date(); const start = new Date(d.getFullYear(), d.getMonth() - 1, 1); const end = new Date(d.getFullYear(), d.getMonth(), 0); return [start, end]; } },
                    ].map((p) => (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => {
                          const [s, e] = p.getRange();
                          setStartDate(toLocalDateString(s));
                          setEndDate(toLocalDateString(e));
                          setDateRangeOpen(false);
                        }}
                        style={{
                          padding: "6px 10px",
                          fontSize: "12px",
                          textAlign: "left",
                          border: "1px solid #e2e8f0",
                          borderRadius: "6px",
                          background: "#fff",
                          cursor: "pointer",
                          color: "#334155",
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                )}
                {dateRangeTab === "days" && (
                  <div style={{ marginBottom: "8px", display: "flex", justifyContent: "center" }}>
                    <DatePicker
                      selected={tempStartDate}
                      onChange={(d) => { setTempStartDate(d); setTempEndDate(d); }}
                      inline
                      monthsShown={1}
                    />
                  </div>
                )}
                {dateRangeTab === "weeks" && (
                  <div style={{ marginBottom: "8px", display: "flex", justifyContent: "center" }}>
                    <DatePicker
                      selected={tempStartDate}
                      onChange={(d) => {
                        if (d) {
                          const start = new Date(d);
                          const day = start.getDay();
                          const diff = start.getDate() - day + (day === 0 ? -6 : 1);
                          start.setDate(diff);
                          const end = new Date(start);
                          end.setDate(end.getDate() + 6);
                          setTempStartDate(start);
                          setTempEndDate(end);
                        }
                      }}
                      inline
                      monthsShown={1}
                    />
                  </div>
                )}
                {dateRangeTab === "months" && (
                  <div style={{ marginBottom: "8px", display: "flex", justifyContent: "center" }} className="worksheet-month-picker">
                    <DatePicker
                      selected={tempStartDate}
                      onChange={(d) => {
                        if (d) {
                          const start = new Date(d.getFullYear(), d.getMonth(), 1);
                          const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                          setTempStartDate(start);
                          setTempEndDate(end);
                        }
                      }}
                      showMonthYearPicker
                      inline
                      monthsShown={1}
                    />
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", alignItems: "center", marginTop: "2px" }}>
                  <button
                    type="button"
                    onClick={() => setDateRangeOpen(false)}
                    style={{
                      padding: "5px 12px",
                      fontSize: "12px",
                      border: "none",
                      background: "transparent",
                      color: "#8b1634",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (tempStartDate) {
                        setStartDate(toLocalDateString(tempStartDate));
                        setEndDate(tempEndDate ? toLocalDateString(tempEndDate) : toLocalDateString(tempStartDate));
                      }
                      setDateRangeOpen(false);
                    }}
                    style={{
                      padding: "5px 12px",
                      fontSize: "12px",
                      border: "none",
                      background: "#8b1634",
                      color: "#fff",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "14px", fontWeight: "500", color: "#4a5568", whiteSpace: "nowrap" }}>
              Filter by worker:
            </span>
          <div ref={workerDropdownRef} style={{ position: "relative", minWidth: "200px", flexShrink: 0 }}>
            <input
              type="text"
              value={workerSearch}
              onFocus={() => setWorkerDropdownOpen(true)}
              onChange={(e) => {
                const val = e.target.value;
                setWorkerSearch(val);
                setWorkerDropdownOpen(true);
              }}
              placeholder="Search worker"
              style={{
                padding: "8px 32px 8px 12px",
                borderRadius: "8px",
                border: workerDropdownOpen ? "2px solid #8b1634" : "1px solid #e2e8f0",
                fontSize: "14px",
                width: "100%",
                minWidth: 0,
                outline: "none",
                boxShadow: workerDropdownOpen ? "0 0 0 1px rgba(139, 22, 52, 0.1)" : "none",
              }}
            />
            {(selectedUserId || workerSearch) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedUserId("");
                  setWorkerSearch("");
                  setWorkerDropdownOpen(false);
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: 0,
                  color: "#4a5568",
                  fontSize: "14px",
                }}
              >
                ×
              </button>
            )}
            {workerDropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  marginTop: "4px",
                  background: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
                  maxHeight: "260px",
                  overflowY: "auto",
                  zIndex: 20,
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {workerOptions
                  .filter((w) =>
                    !workerSearch
                      ? true
                      : w.name.toLowerCase().includes(workerSearch.toLowerCase())
                  )
                  .map((w) => (
                    <div
                      key={w.id}
                      onClick={() => {
                        setSelectedUserId(w.id);
                        setWorkerSearch(w.name);
                        setWorkerDropdownOpen(false);
                      }}
                      style={{
                        padding: "8px 12px",
                        fontSize: "14px",
                        cursor: "pointer",
                        color: "#1a202c",
                        borderBottom: "1px solid #f1f5f9",
                        background:
                          selectedUserId === w.id ? "rgba(139, 22, 52, 0.08)" : "#ffffff",
                      }}
                    >
                      {w.name}
                    </div>
                  ))}
                {workerOptions.length === 0 && (
                  <div
                    style={{
                      padding: "8px 12px",
                      fontSize: "13px",
                      color: "#a0aec0",
                    }}
                  >
                    No workers found
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "14px", fontWeight: "500", color: "#4a5568", whiteSpace: "nowrap" }}>
              Work status:
            </span>
            <select
              value={workStatusFilter}
              onChange={(e) => setWorkStatusFilter(e.target.value)}
              style={{
                padding: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                fontSize: "14px",
                minWidth: "140px",
                outline: "none",
              }}
            >
              <option value="">All</option>
              <option value="on_duty">Shift Started</option>
              <option value="off_duty">Completed</option>
            </select>
          </div>
          </div>
          {(startDate || endDate || selectedUserId) && (
            <button
              type="button"
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setSelectedUserId("");
                setWorkStatusFilter("");
                setWorkerSearch("");
                setWorkerDropdownOpen(false);
              }}
              style={{
                padding: "8px 14px",
                borderRadius: "8px",
                border: "none",
                background: "#e53e3e",
                fontSize: "13px",
                fontWeight: 500,
                color: "#fff",
                cursor: "pointer",
                marginLeft: "auto",
              }}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      <div style={{ overflowX: "auto", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridCols,
            minWidth: tableMinWidth,
            width: "fit-content",
            background: "#e8f4fc",
            padding: "12px 16px",
            borderBottom: "1px solid #e2e8f0",
            gap: "16px",
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Site</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Date</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Work status</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Breaks</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Workload</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Attention</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Break Frequency">Break every</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Break Average Length">Break avg len</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Continuous Work Length">Max continuous</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Break after Continuous Work">Break after</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>On Duty</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Off Duty</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Commuting Time</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Duty Length</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Average duty per day">Avg duty/day</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Cumulative component">Cumulative</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Duty timing component">Duty timing</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Job type / Breaks component">Job/Breaks comp</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Risk Index</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Fatigue Index</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Alert</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Updated Date</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Actions</div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#8b1634", fontWeight: "600" }}>
            Loading work sheets...
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#e53e3e" }}>{error}</div>
        ) : filteredSheets.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#718096" }}>
            {appUsers.length === 0 ? "No workers. Add users in App Users first." : "No work sheets found for the selected worker."}
          </div>
        ) : (
          displayRows.map((row, idx) => {
            const s = row.sheet;
            const updatedDate = s.updatedAt || s.createdAt || row.date;
            const workStatus = row.entry?.work_status || "";
            const entryDayType = row.entry?.dayType || "working";
            const showOnRest =
              (!row.__onRestAssignedReplica && row.entry?.isOnRest === true) ||
              (!row.__onRestAssignedReplica &&
                restHighlight &&
                row.entry?._id &&
                String(restHighlight.entryId) === String(row.entry._id));
            const workerNameFromEntry = getEntryWorkerName(row.entry);
            const workerNameForSite = workerNameFromEntry !== "—" ? workerNameFromEntry : getWorkerNameById(getPrimaryWorkerId(s));
            const workStatusLabel = getWorkStatusLabel(workStatus, showOnRest, entryDayType);
            const workStatusColors = showOnRest
              ? { bg: "rgba(245, 158, 11, 0.16)", text: "#9a3412" }
              : entryDayType === "sick"
                ? { bg: "rgba(239, 68, 68, 0.14)", text: "#b91c1c" }
                : entryDayType === "holiday"
                  ? { bg: "rgba(129, 140, 248, 0.16)", text: "#4338ca" }
                  : entryDayType === "college"
                    ? { bg: "rgba(59, 130, 246, 0.14)", text: "#1d4ed8" }
                    : workStatus === "on_duty"
                ? { bg: "rgba(16, 185, 129, 0.14)", text: "#047857" }
                : workStatus === "off_duty"
                ? { bg: "rgba(107, 114, 128, 0.14)", text: "#374151" }
                : workStatus === "break"
                ? { bg: "rgba(59, 130, 246, 0.14)", text: "#1d4ed8" }
                : workStatus === "resume"
                ? { bg: "rgba(234, 179, 8, 0.16)", text: "#92400e" }
                : workStatus === "sleep"
                ? { bg: "rgba(129, 140, 248, 0.16)", text: "#4338ca" }
                : { bg: "rgba(148, 163, 184, 0.16)", text: "#475569" };
            const { rowBg, rowHoverBg, rowBorderLeft } = getWorksheetRowBackground(entryDayType, showOnRest);
            const siteTitle =
              getRowSiteName(s, row.entry) +
              (workerNameForSite && workerNameForSite !== "—" ? ` (${workerNameForSite})` : "");
            return (
              <div
                key={`${s._id}-${row.date.getTime()}-${row.entry?._id || idx}`}
                onClick={() => openEditModal(row)}
                title="Click to edit"
                style={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  minWidth: tableMinWidth,
                  width: "fit-content",
                  padding: "16px",
                  borderBottom: "1px solid #f1f5f9",
                  gap: "16px",
                  alignItems: "center",
                  background: rowBg,
                  borderLeft: rowBorderLeft,
                  cursor: "pointer",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = rowHoverBg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = rowBg;
                }}
              >
                <>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }} title={siteTitle}>
                    {getRowSiteName(s, row.entry)}
                    {workerNameForSite && workerNameForSite !== "—" ? ` (${workerNameForSite})` : ""}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{formatDate(row.date)}</div>
                  <div
                    style={{
                      ...cellEllipsis,
                      fontSize: "13px",
                      fontWeight: 600,
                      color: workStatusColors.text,
                      background: workStatus === "" && !showOnRest ? "transparent" : workStatusColors.bg,
                      borderRadius: "999px",
                      padding: "4px 10px",
                      textAlign: "center",
                      justifySelf: "flex-start",
                    }}
                    title={
                      showOnRest && workerNameFromEntry
                        ? `${workStatusLabel}: ${workerNameFromEntry}`
                        : workStatusLabel
                    }
                  >
                    {workStatusLabel}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }} title={getSheetBreakTime(s, row.entry)}>
                    {getSheetBreakTime(s, row.entry)}
                  </div>
                  <div
                    style={{ color: "#2d3748", fontSize: "14px", whiteSpace: "normal", lineHeight: 1.2 }}
                    title={getWorkloadLabel(row.entry?.workload ?? s.workload)}
                  >
                    {getWorkloadLabel(row.entry?.workload ?? s.workload)}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }} title={getAttentionLabel(row.entry?.attention ?? s.attention)}>
                    {getAttentionLabel(row.entry?.attention ?? s.attention)}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>
                    {(row.entry?.break_frequency ?? s.break_frequency) != null ? `${row.entry?.break_frequency ?? s.break_frequency} min` : "—"}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>
                    {(row.entry?.break_average_length ?? s.break_average_length) != null ? `${row.entry?.break_average_length ?? s.break_average_length} min` : "—"}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>
                    {(row.entry?.continuous_work_length ?? s.continuous_work_length) != null ? `${row.entry?.continuous_work_length ?? s.continuous_work_length} min` : "—"}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>
                    {(row.entry?.break_after_continuous_work ?? s.break_after_continuous_work) != null ? `${row.entry?.break_after_continuous_work ?? s.break_after_continuous_work} min` : "—"}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }} title={getSheetOnDuty(s, row.entry)}>
                    {getSheetOnDuty(s, row.entry)}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }} title={getSheetOffDuty(s, row.entry)}>
                    {getSheetOffDuty(s, row.entry)}
                  </div>
                  <div
                    style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}
                    title={
                      row.entry?.travel_time != null && Number(row.entry.travel_time) > 0
                        ? `${row.entry.travel_time} min`
                        : ""
                    }
                  >
                    {row.entry?.travel_time != null && Number(row.entry.travel_time) > 0
                      ? formatMinutesAsHoursMinutes(row.entry.travel_time)
                      : "—"}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>
                    {displayDutyLengthCell(row.entry || s)}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>
                    {formatRoundedValue(row.entry?.avgDutyPerDay, 2)}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{row.entry?.cumulative != null ? row.entry.cumulative : "—"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{row.entry?.dutyTiming != null ? row.entry.dutyTiming : "—"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{row.entry?.jobTypeBreaksComponent != null ? row.entry.jobTypeBreaksComponent : "—"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{row.entry?.riskIndex != null ? row.entry.riskIndex : "—"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{row.entry?.fatigueIndex != null ? row.entry.fatigueIndex : "—"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px", whiteSpace: "normal", lineHeight: 1.2 }} title={getAlertLabel(row.entry?.alert)}>
                    {getAlertLabel(row.entry?.alert)}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{formatDate(updatedDate)}</div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(row);
                      }}
                      title="Edit"
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: "6px",
                        color: "#22c55e",
                        fontSize: "16px",
                      }}
                    >
                      <i className="fa fa-edit" />
                    </button>
                    {Array.isArray(row.entry?.visits) && row.entry.visits.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/fatigue/work-sheets/visits/${row.entry._id}`, {
                            state: {
                              siteName: getRowSiteName(row.sheet, row.entry),
                              date: row.date,
                              workerName: getEntryWorkerName(row.entry),
                              visits: (row.entry.visits || []).map((v) => ({
                                ...v,
                                siteName: sites.find((s) => String(s._id) === String(v.siteId))?.siteName || v.siteId || "",
                              })),
                            },
                          });
                        }}
                        title={`View Site Visits (${row.entry.visits.length})`}
                        style={{
                          background: "transparent",
                          border: "1px solid #8b1634",
                          borderRadius: "8px",
                          cursor: "pointer",
                          padding: "4px 10px",
                          color: "#8b1634",
                          fontSize: "13px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        View Visit
                      </button>
                    )}
                    {Array.isArray(row.entry?.onCallEntries) && row.entry.onCallEntries.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openOnCallPage(row);
                      }}
                      title={`On-Call Emergency (${row.entry.onCallEntries.length})`}
                      style={{
                        border: "1px solid #8b1634",
                        color: "#8b1634",
                        background: "transparent",
                        fontWeight: 600,
                        borderRadius: "6px",
                        padding: "4px 8px",
                        fontSize: "12px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        lineHeight: 1.2,
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#8b1634";
                        e.currentTarget.style.color = "#fff";
                        e.currentTarget.style.borderColor = "#8b1634";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "#8b1634";
                        e.currentTarget.style.borderColor = "#8b1634";
                      }}
                    >
                      <i className="fa fa-eye" style={{ fontSize: "11px" }} />
                      On-Call
                    </button>
                    )}
                    {row.entry?.isExceedance === true && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openExceedanceModal(row);
                        }}
                        title="Fatigue Rule Alert"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "6px",
                          color: "#8b1634",
                          fontSize: "16px",
                        }}
                      >
                        <i className="fa fa-info-circle" />
                      </button>
                    )}
                  </div>
                </>
              </div>
            );
          })
        )}
      </div>
      </>
      )}

      {showExceedanceModal && selectedExceedanceRow && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.35)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "540px", width: "90%" }}>
            <div className="modal-content" style={{ borderRadius: "14px", border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 16px 40px rgba(2, 6, 23, 0.22)" }}>
              <div
                className="modal-header"
                style={{
                  borderBottom: "1px solid #f1f5f9",
                  background: "#fff",
                  padding: "12px 14px",
                }}
              >
                <h5 className="modal-title" style={{ margin: 0, color: "#8b1634", fontSize: "17px", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="fa fa-exclamation-circle" style={{ fontSize: "16px" }} />
                  <span>Fatigue Rule Alert</span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowExceedanceModal(false);
                    setSelectedExceedanceRow(null);
                  }}
                  style={{ opacity: 0.7, transform: "scale(0.9)" }}
                />
              </div>
              <div className="modal-body" style={{ background: "#fff", padding: "14px 14px 8px" }}>
                <div style={{ border: "1px solid #f1f5f9", borderRadius: "10px", padding: "10px 11px", background: "#fcfcfd" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "96px 1fr", rowGap: "7px", columnGap: "10px", color: "#1f2937", fontSize: "14px", lineHeight: 1.35 }}>
                    <div style={{ color: "#6b7280", fontWeight: 600 }}>Site</div>
                    <div style={{ fontWeight: 600 }}>{selectedExceedanceRow.siteName}</div>
                    <div style={{ color: "#6b7280", fontWeight: 600 }}>Date</div>
                    <div style={{ fontWeight: 600 }}>{selectedExceedanceRow.date ? formatDate(selectedExceedanceRow.date) : "—"}</div>
                    <div style={{ color: "#6b7280", fontWeight: 600 }}>On Duty</div>
                    <div style={{ fontWeight: 600 }}>{selectedExceedanceRow.onDuty || "—"}</div>
                    <div style={{ color: "#6b7280", fontWeight: 600 }}>Off Duty</div>
                    <div style={{ fontWeight: 600 }}>{selectedExceedanceRow.offDuty || "—"}</div>
                  </div>
                </div>
                <div style={{ borderTop: "1px solid #f1f5f9", marginTop: "12px", paddingTop: "10px", textAlign: "center" }}>
                  <div style={{ color: "#374151", fontWeight: 700, fontSize: "15px", marginBottom: "2px" }}>Triggered Rules</div>
                  <div style={{ color: "#8b1634", fontSize: "15px", fontWeight: 700, whiteSpace: "pre-wrap", wordBreak: "break-word", minHeight: "18px" }}>
                    {selectedExceedanceRow.triggeredRules}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: "none", background: "#fff", padding: "10px 12px 12px" }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowExceedanceModal(false);
                    setSelectedExceedanceRow(null);
                  }}
                  style={{
                    width: "100%",
                    background: "#8b1634",
                    border: "none",
                    borderRadius: "8px",
                    color: "#fff",
                    fontSize: "15px",
                    fontWeight: 700,
                    padding: "9px 12px",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editSheet && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.4)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content" style={{ maxWidth: "1200px", margin: "0 auto", borderRadius: "12px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", border: "none" }}>
              <div className="modal-header" style={{ borderBottom: "1px solid #e2e8f0", background: "#f8fafc", padding: "16px 20px", borderRadius: "12px 12px 0 0" }}>
                <h5 className="modal-title" style={{ fontWeight: 600, color: "#8b1634", fontSize: "18px" }}>Edit Work Sheet</h5>
                <button type="button" className="btn-close" onClick={() => { setShowEditModal(false); setEditSheet(null); setCopyFieldsToOtherEntries(false); }} style={{ filter: "none", opacity: 0.7 }} />
              </div>
              <div className="modal-body" style={{ maxHeight: "80vh", overflowY: "auto", padding: "24px 20px", background: "#fff" }}>
                {(() => {
                  const hasRisk = editSheet.riskIndex !== "" && editSheet.riskIndex != null;
                  const hasFatigue = editSheet.fatigueIndex !== "" && editSheet.fatigueIndex != null;
                  const locked =
                    editSheet.work_status === "on_duty" ||
                    editSheet.work_status === "off_duty" ||
                    hasRisk ||
                    hasFatigue;
                  return locked ? (
                    <div style={{ marginBottom: "14px", padding: "10px 12px", borderRadius: "10px", background: "rgba(229, 62, 62, 0.08)", border: "1px solid rgba(229, 62, 62, 0.25)", color: "#b91c1c", fontSize: "13px", fontWeight: 600 }}>
                      {editSheet.work_status === "on_duty"
                        ? "Editing disabled because shift is Shift Started."
                        : "Editing disabled because shift is Completed or Risk/Fatigue Index is already set."}
                    </div>
                  ) : null;
                })()}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Site</label>
                  <input
                    type="text"
                    className="form-control"
                    readOnly
                      disabled
                    value={sites.find((st) => st._id === editSheet.siteId)?.siteName || editSheet.siteId || "—"}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px", background: "#f8fafc", color: "#64748b" }}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Date</label>
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      disabled
                      value={editSheet.editDate ? formatDate(editSheet.editDate) : "—"}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px", background: "#f8fafc", color: "#64748b" }}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Workers</label>
                  {editSheet.entryRole === "cover" ? (
                    <>
                      <div
                        style={{
                          marginBottom: "10px",
                          padding: "10px 12px",
                          borderRadius: "10px",
                          background: "rgba(59, 130, 246, 0.08)",
                          border: "1px solid rgba(59, 130, 246, 0.25)",
                          color: "#1d4ed8",
                          fontSize: "13px",
                          fontWeight: 600,
                        }}
                      >
                        This is an assigned cover record. Workers cannot be changed here.
                      </div>
                      <p className="text-muted small mb-2">Cover assignment for this date (working row, not on rest).</p>
                      <div className="mb-2" style={{ fontSize: "14px", color: "#2d3748" }}>
                        <span style={{ fontWeight: 600 }}>Worker on rest: </span>
                        {getWorkerNameById(editSheet.restingWorkerId)}
                      </div>
                      <div style={{ fontWeight: 600, color: "#2d3748", fontSize: "14px", marginBottom: "8px" }}>Cover worker</div>
                      <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", background: "rgba(139,22,52,0.04)" }}>
                        {availableWorkersForCoverEntryEdit.map((w) => (
                          <div key={w._id} className="form-check" style={{ marginBottom: "6px" }}>
                            <input
                              className="form-check-input theme-checkbox"
                              type="radio"
                              name="edit-worksheet-cover-only"
                              checked={String(editSheet.coverWorkerId) === String(w._id)}
                              disabled
                              onChange={() => {
                                setEditSheet({ ...editSheet, coverWorkerId: w._id, workerIds: [w._id] });
                              }}
                            />
                            <label className="form-check-label" style={{ color: "#2d3748", fontWeight: 500, fontSize: "14px" }}>
                              {w.firstName} {w.lastName} ({w.email})
                            </label>
                          </div>
                        ))}
                        {availableWorkersForCoverEntryEdit.length === 0 ? (
                          <p className="text-muted small mb-0">No workers available for cover on this date.</p>
                        ) : null}
                      </div>
                    </>
                  ) : appUsers.length === 0 ? (
                    <p className="text-muted small">No app users.</p>
                  ) : (
                    <>
                      {editSheet.onRestLocked && editSheet.isOnRest ? (
                        <div
                          style={{
                            marginBottom: "10px",
                            padding: "10px 12px",
                            borderRadius: "10px",
                            background: "rgba(59, 130, 246, 0.08)",
                            border: "1px solid rgba(59, 130, 246, 0.25)",
                            color: "#1d4ed8",
                            fontSize: "13px",
                            fontWeight: 600,
                          }}
                        >
                          This date is already marked On Rest. The worker on rest cannot be changed.
                        </div>
                      ) : null}
                      {editSheet.isCoverAssigneeRow && !editSheet.isOnRest ? (
                        <div
                          style={{
                            marginBottom: "10px",
                            padding: "10px 12px",
                            borderRadius: "10px",
                            background: "rgba(59, 130, 246, 0.08)",
                            border: "1px solid rgba(59, 130, 246, 0.25)",
                            color: "#1d4ed8",
                            fontSize: "13px",
                            fontWeight: 600,
                          }}
                        >
                          This worker is assigned as cover for someone on rest on this date. Mark as On Rest is not available here.
                        </div>
                      ) : null}
                      <div className="mb-2" style={{ fontSize: "14px", color: "#2d3748" }}>
                        <span style={{ fontWeight: 600 }}>{editSheet.isOnRest ? "Worker on rest: " : "Worker: "}</span>
                        {editSheet.restingWorkerId ? getWorkerNameById(editSheet.restingWorkerId) : "—"}
                      </div>
                      {!editSheet.isOnRest ? (
                        <div className="mb-3">
                          <div className="form-check" style={{ marginBottom: "10px" }}>
                            <input
                              className="form-check-input theme-checkbox"
                              type="checkbox"
                              id="edit-worksheet-holiday-type-enabled"
                              checked={Boolean(editSheet.holidayTypeEnabled)}
                              disabled={
                                editSheet.work_status === "on_duty" ||
                                editSheet.work_status === "off_duty" ||
                                editModalLoading
                              }
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEditSheet((prev) => ({
                                  ...prev,
                                  holidayTypeEnabled: checked,
                                  dayType: checked ? (isNonWorkingDayType(prev.dayType) ? prev.dayType : "") : "working",
                                  holidayTypeReason: checked ? prev.holidayTypeReason : "",
                                  ...(checked && isNonWorkingDayType(prev.dayType)
                                    ? {
                                        onDutyTime: "",
                                        offDutyTime: "",
                                        break_time: 0,
                                        travel_time: 0,
                                        dutyLengthMinutes: 0,
                                      }
                                    : {}),
                                }));
                              }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="edit-worksheet-holiday-type-enabled"
                              style={{ color: "#2d3748", fontWeight: 600, fontSize: "14px" }}
                            >
                              Mark Holiday Type
                            </label>
                          </div>
                          <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>
                            Holiday Type
                          </label>
                          <select
                            className="form-select"
                            value={editSheet.dayType || ""}
                            onChange={(e) => {
                              const nextDayType = e.target.value;
                              setEditSheet((prev) => ({
                                ...prev,
                                dayType: nextDayType,
                                holidayTypeReason:
                                  nextDayType === "sick" || nextDayType === "holiday" ? prev.holidayTypeReason : "",
                                ...(isNonWorkingDayType(nextDayType)
                                  ? {
                                      onDutyTime: "",
                                      offDutyTime: "",
                                      break_time: 0,
                                      travel_time: 0,
                                      dutyLengthMinutes: 0,
                                    }
                                  : {}),
                              }));
                            }}
                            disabled={
                              !editSheet.holidayTypeEnabled ||
                              editSheet.work_status === "on_duty" ||
                              editSheet.work_status === "off_duty" ||
                              editModalLoading
                            }
                            style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                          >
                            <option value="">Select Holiday Type</option>
                            {HOLIDAY_TYPE_SELECT_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          {editSheet.holidayTypeEnabled && editSheet.dayType === "sick" ? (
                            <div className="mt-3">
                              <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>
                                Reason for Sick
                              </label>
                              <textarea
                                className="form-control"
                                rows={3}
                                maxLength={500}
                                value={editSheet.holidayTypeReason || ""}
                                onChange={(e) => setEditSheet({ ...editSheet, holidayTypeReason: e.target.value })}
                                disabled={editModalLoading || editSheet.work_status === "off_duty"}
                                placeholder="Enter reason for sick day"
                                style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                              />
                            </div>
                          ) : null}
                          {editSheet.holidayTypeEnabled && editSheet.dayType === "holiday" ? (
                            <div className="mt-3">
                              <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>
                                Reason for Holiday
                              </label>
                              <textarea
                                className="form-control"
                                rows={3}
                                maxLength={500}
                                value={editSheet.holidayTypeReason || ""}
                                onChange={(e) => setEditSheet({ ...editSheet, holidayTypeReason: e.target.value })}
                                disabled={editModalLoading || editSheet.work_status === "off_duty"}
                                placeholder="Enter reason for holiday"
                                style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                              />
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="form-check" style={{ marginBottom: "10px" }}>
                        <input
                          className="form-check-input theme-checkbox"
                          type="checkbox"
                          id="edit-worksheet-on-rest"
                          checked={Boolean(editSheet.isOnRest)}
                          disabled={
                            editSheet.work_status === "on_duty" ||
                            editSheet.work_status === "off_duty" ||
                            (editSheet.onRestLocked && editSheet.isOnRest) ||
                            editSheet.isCoverAssigneeRow
                          }
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setEditSheet((prev) => {
                              const sh = sheets.find((x) => String(x._id) === String(prev._id));
                              const primary = sh ? getPrimaryWorkerId(sh) : "";
                              return {
                                ...prev,
                                isOnRest: checked,
                                restingWorkerId: prev.restingWorkerId || primary || "",
                                coverWorkerId: checked ? prev.coverWorkerId : "",
                              };
                            });
                          }}
                        />
                        <label className="form-check-label" htmlFor="edit-worksheet-on-rest" style={{ color: "#2d3748", fontWeight: 600, fontSize: "14px" }}>
                          Mark as On Rest
                        </label>
                      </div>
                      {editSheet.isOnRest ? (
                        <>
                          <div style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "8px" }}>
                            Assign cover worker (required)
                          </div>
                          <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", background: "rgba(139,22,52,0.04)" }}>
                            {availableCoverWorkers.map((w) => (
                              <div key={w._id} className="form-check" style={{ marginBottom: "6px" }}>
                                <input
                                  className="form-check-input theme-checkbox"
                                  type="radio"
                                  name="edit-worksheet-cover"
                                  checked={String(editSheet.coverWorkerId) === String(w._id)}
                                  disabled={
                                    editSheet.work_status === "on_duty" ||
                                    editSheet.work_status === "off_duty" ||
                                    (editSheet.onRestLocked && editSheet.isOnRest)
                                  }
                                  onChange={() => {
                                    setEditSheet({ ...editSheet, coverWorkerId: w._id });
                                  }}
                                />
                                <label className="form-check-label" style={{ color: "#2d3748", fontWeight: 500, fontSize: "14px" }}>
                                  {w.firstName} {w.lastName} ({w.email})
                                </label>
                              </div>
                            ))}
                            {availableCoverWorkers.length === 0 ? (
                              <p className="text-muted small mb-0">No workers available to assign as cover for this date.</p>
                            ) : null}
                            {!editSheet.coverWorkerId ? (
                              <p className="text-muted small mb-0 mt-2">Select a different worker than the one on rest.</p>
                            ) : null}
                          </div>
                        </>
                      ) : null}
                    </>
                  )}
                </div>
                
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Workload</label>
                    <select
                      className="form-select"
                      value={editSheet.workload}
                      onChange={(e) => setEditSheet({ ...editSheet, workload: e.target.value })}
                      disabled={editSheet.work_status === "on_duty" || editSheet.work_status === "off_duty"}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    >
                      <option value={0}>Extremely demanding, no spare capacity</option>
                      <option value={1}>Moderately demanding, little spare capacity</option>
                      <option value={2}>Moderately undemanding, some spare capacity</option>
                      <option value={3}>Extremely undemanding, lots of spare capacity</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Attention</label>
                    <select
                      className="form-select"
                      value={editSheet.attention}
                      onChange={(e) => setEditSheet({ ...editSheet, attention: e.target.value })}
                      disabled={editSheet.work_status === "on_duty" || editSheet.work_status === "off_duty"}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    >
                      <option value={0}>All or nearly all the time</option>
                      <option value={1}>Most of the Time</option>
                      <option value={2}>Some of the time</option>
                      <option value={3}>Rarely or Nearly none of the time</option>
                    </select>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Break Frequency (min)</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      placeholder="180"
                      value={editSheet.break_frequency}
                      onChange={(e) => setEditSheet({ ...editSheet, break_frequency: e.target.value })}
                      disabled={editSheet.work_status === "on_duty" || editSheet.work_status === "off_duty"}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Break Average Length (min)</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      placeholder="15"
                      value={editSheet.break_average_length}
                      onChange={(e) => setEditSheet({ ...editSheet, break_average_length: e.target.value })}
                      disabled={editSheet.work_status === "on_duty" || editSheet.work_status === "off_duty"}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Continuous Work Length (min)</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      placeholder="360"
                      value={editSheet.continuous_work_length}
                      onChange={(e) => setEditSheet({ ...editSheet, continuous_work_length: e.target.value })}
                      disabled={editSheet.work_status === "on_duty" || editSheet.work_status === "off_duty"}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Break after Continuous Work (min)</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      placeholder="30"
                      value={editSheet.break_after_continuous_work}
                      onChange={(e) => setEditSheet({ ...editSheet, break_after_continuous_work: e.target.value })}
                      disabled={editSheet.work_status === "on_duty" || editSheet.work_status === "off_duty"}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    />
                  </div>
                </div>
                {(() => {
                  const nonWorkingDayLocked = Boolean(editSheet.holidayTypeEnabled) && isNonWorkingDayType(editSheet.dayType);
                  const shiftFieldsLocked = isEditSheetShiftFieldsLocked(editSheet) || nonWorkingDayLocked;
                  const copyWorkerId = getEditSheetWorkerId(editSheet);
                  const copyTargetCount = getCopyTargetCount(editSheet.entryId, editSheet.siteId, copyWorkerId);
                  const copyDisabled =
                    editModalLoading ||
                    shiftFieldsLocked ||
                    editSheet.isOnRest ||
                    !copyWorkerId ||
                    copyTargetCount === 0;
                  const copyFieldsError = copyFieldsToOtherEntries ? getCopyShiftFieldsError(editSheet) : null;
                  return (
                    <div
                      style={{
                        marginBottom: "20px",
                        border: "1px solid #edd5df",
                        borderRadius: "14px",
                        background: "linear-gradient(180deg, #fff9fb 0%, #ffffff 100%)",
                        overflow: "hidden",
                        boxShadow: "0 6px 18px rgba(139,22,52,0.06)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          padding: "14px 16px",
                          borderBottom: "1px solid #f3dce4",
                          background: "rgba(139,22,52,0.05)",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div
                            style={{
                              width: "34px",
                              height: "34px",
                              borderRadius: "10px",
                              background: "#8b1634",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "15px",
                            }}
                          >
                            <i className="fa fa-clock-o" />
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, color: "#8b1634", fontSize: "15px" }}>Shift Time Details</div>
                            <div style={{ color: "#7b314f", fontSize: "12px", marginTop: "2px" }}>
                              Breaks, duty times and commuting for this entry
                            </div>
                          </div>
                        </div>
                      </div>
                      <div style={{ padding: "16px" }}>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Breaks (min)</label>
                            <MinuteStepInput
                              id="edit-sheet-break-time"
                              value={editSheet.break_time}
                              onChange={(break_time) => setEditSheet({ ...editSheet, break_time })}
                              disabled
                              max={MAX_BREAK_MINUTES}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Commuting Time (min)</label>
                            <MinuteStepInput
                              id="edit-sheet-travel-time"
                              value={editSheet.travel_time}
                              onChange={(travel_time) => setEditSheet({ ...editSheet, travel_time })}
                              disabled={editModalLoading || shiftFieldsLocked}
                              max={MAX_COMMUTING_MINUTES}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-0">
                            <label htmlFor="edit-sheet-on-duty-time" className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>On Duty</label>
                            <AnalogClockTimePicker
                              id="edit-sheet-on-duty-time"
                              value={editSheet.onDutyTime}
                              onChange={(onDutyTime) => setEditSheet({ ...editSheet, onDutyTime })}
                              disabled={editModalLoading || shiftFieldsLocked}
                              isClearable={!(editModalLoading || shiftFieldsLocked)}
                              placeholder="Select time"
                              aria-label="On Duty time"
                            />
                          </div>
                          <div className="col-md-6 mb-0">
                            <label htmlFor="edit-sheet-off-duty-time" className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Off Duty</label>
                            <AnalogClockTimePicker
                              id="edit-sheet-off-duty-time"
                              value={editSheet.offDutyTime}
                              onChange={(offDutyTime) => setEditSheet({ ...editSheet, offDutyTime })}
                              disabled={editModalLoading || shiftFieldsLocked}
                              isClearable={!(editModalLoading || shiftFieldsLocked)}
                              placeholder="Select time"
                              aria-label="Off Duty time"
                            />
                          </div>
                        </div>
                        <div
                          style={{
                            marginTop: "14px",
                            padding: "12px 14px",
                            borderRadius: "12px",
                            border: copyFieldsToOtherEntries ? "1px solid #d9a7b8" : "1px solid #f1dce4",
                            background: copyFieldsToOtherEntries ? "rgba(139,22,52,0.08)" : "#fcfcfd",
                          }}
                        >
                          <label
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "10px",
                              margin: 0,
                              cursor: copyDisabled ? "not-allowed" : "pointer",
                              opacity: copyDisabled ? 0.65 : 1,
                            }}
                          >
                            <input
                              className="form-check-input theme-checkbox"
                              type="checkbox"
                              checked={copyFieldsToOtherEntries}
                              disabled={copyDisabled}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const err = getCopyShiftFieldsError(editSheet);
                                  if (err) {
                                    toast.error(err);
                                    return;
                                  }
                                }
                                setCopyFieldsToOtherEntries(e.target.checked);
                              }}
                              style={{ marginTop: "3px" }}
                            />
                            <span>
                              <span style={{ display: "block", fontWeight: 700, color: "#8b1634", fontSize: "14px" }}>
                                Copy to same site & worker entries on Save
                              </span>
                              <span style={{ display: "block", color: "#6b7280", fontSize: "12px", marginTop: "4px", lineHeight: 1.45 }}>
                                {shiftFieldsLocked
                                  ? "Copy is disabled because this entry is Completed or has Risk/Fatigue Index set."
                                  : editSheet.isOnRest
                                    ? "Copy is not available for On Rest entries."
                                    : copyTargetCount > 0
                                      ? `These 4 values will be applied to ${copyTargetCount} other ${copyTargetCount === 1 ? "entry" : "entries"} for this site and worker when you click Save.`
                                      : "No other eligible entries found for this site and worker."}
                              </span>
                              {copyFieldsError ? (
                                <span style={{ display: "block", color: "#dc2626", fontSize: "12px", marginTop: "6px", fontWeight: 600 }}>
                                  {copyFieldsError}
                                </span>
                              ) : null}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {(() => {
                  const siteVisitsDisabled = Boolean(editSheet.holidayTypeEnabled) || Boolean(editSheet.isOnRest) || editSheet.work_status === "off_duty";
                  const siteVisitsOn = !siteVisitsDisabled && (editSheet.siteVisitsEnabled ?? ((editSheet.visits || []).length > 0));
                  return (
                    <div
                      className="mb-3"
                      style={{ border: "1px solid #edd5df", borderRadius: "14px", padding: "16px", background: "#fff9fb", opacity: siteVisitsDisabled ? 0.6 : 1 }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                        <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", margin: 0, display: "flex", alignItems: "center", gap: "8px", cursor: siteVisitsDisabled ? "not-allowed" : "pointer" }}>
                          <input
                            type="checkbox"
                            checked={siteVisitsOn}
                            disabled={siteVisitsDisabled}
                            onChange={(e) => setEditSheet({ ...editSheet, siteVisitsEnabled: e.target.checked })}
                            style={{ width: "16px", height: "16px", accentColor: "#8b1634", cursor: siteVisitsDisabled ? "not-allowed" : "pointer" }}
                          />
                          Site Visits
                        </label>
                        {siteVisitsOn && (
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{ background: "#8b1634", color: "#fff", borderRadius: "8px", fontSize: "13px" }}
                            onClick={() =>
                              setEditSheet({
                                ...editSheet,
                                visits: [
                                  ...(editSheet.visits || []),
                                  { siteId: "", travelTime: 15, timeOn: "", timeOff: "", status: "scheduled" },
                                ],
                              })
                            }
                          >
                            + Add Visit
                          </button>
                        )}
                      </div>
                      {siteVisitsDisabled ? (
                        (editSheet.visits || []).length > 0 ? (
                          <div>
                            <p className="text-muted small mb-2">Editing disabled (Holiday / On Rest / Completed). Existing visits are shown below.</p>
                            {(editSheet.visits || []).map((visit, vIdx) => (
                              <div className="row g-2 align-items-center mb-2" key={vIdx} style={{ fontSize: "13px", color: "#2d3748" }}>
                                <div className="col-md-4"><span className="text-muted small">Site: </span>{sites.find((s) => String(s._id) === String(visit.siteId))?.siteName || visit.siteId || "—"}</div>
                                <div className="col-md-2"><span className="text-muted small">On: </span>{visit.timeOn || "—"}</div>
                                <div className="col-md-2"><span className="text-muted small">Off: </span>{visit.timeOff || "—"}</div>
                                <div className="col-md-2"><span className="text-muted small">Travel: </span>{visit.travelTime ?? "—"}</div>
                                <div className="col-md-2">
                                  {visit.status === "confirmed" ? (
                                    <span style={{ fontSize: "11px", fontWeight: 700, color: "#1b5e20", background: "#e6f4ea", border: "1px solid #b7dfc2", borderRadius: "6px", padding: "2px 8px" }}>✓ Completed</span>
                                  ) : (
                                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#92400e", background: "#fef3c7", border: "1px solid #fcd9a3", borderRadius: "6px", padding: "2px 8px" }}>Scheduled</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted small mb-0">Site Visits are not available when Holiday Type or On Rest is selected.</p>
                        )
                      ) : siteVisitsOn ? (
                        (editSheet.visits || []).length === 0 ? (
                          <p className="text-muted small mb-0">No visits added. The worker confirms these from the app.</p>
                        ) : (
                          (editSheet.visits || []).map((visit, vIdx) => (
                            <div className="row g-2 align-items-end mb-2" key={vIdx}>
                              <div className="col-md-4">
                                <label className="form-label small mb-1">Site</label>
                                <SearchableSiteSelect
                                  value={visit.siteId || ""}
                                  placeholder="Select site"
                                  options={sites
                                    .filter((st) => st._id !== editSheet.siteId || st._id === visit.siteId)
                                    .map((st) => ({ value: st._id, label: st.siteName || st._id }))}
                                  onChange={(siteId) => {
                                    const next = [...editSheet.visits];
                                    next[vIdx] = { ...next[vIdx], siteId };
                                    setEditSheet({ ...editSheet, visits: next });
                                  }}
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label small mb-1">Site On</label>
                                <AnalogClockTimePicker
                                  value={visit.timeOn || ""}
                                  onChange={(timeOn) => {
                                    const next = [...editSheet.visits];
                                    next[vIdx] = { ...next[vIdx], timeOn };
                                    setEditSheet({ ...editSheet, visits: next });
                                  }}
                                  isClearable
                                  placeholder="Select time"
                                  aria-label="Site On time"
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label small mb-1">Site Off</label>
                                <AnalogClockTimePicker
                                  value={visit.timeOff || ""}
                                  onChange={(timeOff) => {
                                    const next = [...editSheet.visits];
                                    next[vIdx] = { ...next[vIdx], timeOff };
                                    setEditSheet({ ...editSheet, visits: next });
                                  }}
                                  isClearable
                                  placeholder="Select time"
                                  aria-label="Site Off time"
                                />
                              </div>
                              <div className="col-md-3">
                                <label className="form-label small mb-1">Travel Time (min)</label>
                                <input
                                  type="number"
                                  min={0}
                                  className="form-control form-control-sm"
                                  placeholder="e.g. 30"
                                  value={visit.travelTime ?? ""}
                                  onChange={(e) => {
                                    const next = [...editSheet.visits];
                                    next[vIdx] = { ...next[vIdx], travelTime: e.target.value };
                                    setEditSheet({ ...editSheet, visits: next });
                                  }}
                                />
                              </div>
                              <div className="col-md-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger w-100"
                                  title="Remove visit"
                                  onClick={() => {
                                    const next = (editSheet.visits || []).filter((_, i) => i !== vIdx);
                                    setEditSheet({ ...editSheet, visits: next });
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                              {visit.status === "confirmed" ? (
                                <div className="col-12">
                                  <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#1b5e20", background: "#e6f4ea", border: "1px solid #b7dfc2", borderRadius: "6px", padding: "2px 8px", display: "inline-block" }}>✓ Completed by worker</span>
                                </div>
                              ) : null}
                            </div>
                          ))
                        )
                      ) : null}
                    </div>
                  );
                })()}
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Duty Length (min)</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      placeholder="0"
                      value={editSheet.dutyLengthMinutes}
                      onChange={(e) => setEditSheet({ ...editSheet, dutyLengthMinutes: e.target.value })}
                      disabled
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                      onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Average duty per day</label>
                    <input
                      type="number"
                      className="form-control"
                      step="any"
                      placeholder="0"
                      value={round2(editSheet.avgDutyPerDay)}
                      onChange={(e) => setEditSheet({ ...editSheet, avgDutyPerDay: e.target.value })}
                      disabled
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                      onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Cumulative component</label>
                    <input
                      type="number"
                      className="form-control"
                      step="any"
                      placeholder="0"
                      value={round2(editSheet.cumulative)}
                      onChange={(e) => setEditSheet({ ...editSheet, cumulative: e.target.value })}
                      disabled
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                      onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Duty timing component</label>
                    <input
                      type="number"
                      className="form-control"
                      step="any"
                      placeholder="0"
                      value={round2(editSheet.dutyTiming)}
                      onChange={(e) => setEditSheet({ ...editSheet, dutyTiming: e.target.value })}
                      disabled
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                      onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Job type / Breaks component</label>
                    <input
                      type="number"
                      className="form-control"
                      step="any"
                      placeholder="0"
                      value={round2(editSheet.jobTypeBreaksComponent)}
                      onChange={(e) => setEditSheet({ ...editSheet, jobTypeBreaksComponent: e.target.value })}
                      disabled
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                      onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Risk Index</label>
                    <input
                      type="number"
                      className="form-control"
                      step="any"
                      placeholder="0"
                      value={round2(editSheet.riskIndex)}
                      onChange={(e) => setEditSheet({ ...editSheet, riskIndex: e.target.value })}
                      disabled
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                      onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Fatigue Index</label>
                    <input
                      type="number"
                      className="form-control"
                      step="any"
                      placeholder="0"
                      value={round2(editSheet.fatigueIndex)}
                      onChange={(e) => setEditSheet({ ...editSheet, fatigueIndex: e.target.value })}
                      disabled
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                      onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Alert</label>
                    <select
                      className="form-select"
                      value={editSheet.alert != null && editSheet.alert !== "" ? String(editSheet.alert) : ""}
                      disabled
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    >
                      <option value="">—</option>
                      {ALERT_LEVEL_OPTIONS.map((o) => (
                        <option key={o.level} value={String(o.level)}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Name (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Week 1 - Phase 16"
                    value={editSheet.name || ""}
                    onChange={(e) => setEditSheet({ ...editSheet, name: e.target.value })}
                    disabled
                    style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Notes (optional)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Operational context..."
                    value={editSheet.notes || ""}
                    onChange={(e) => setEditSheet({ ...editSheet, notes: e.target.value })}
                    disabled
                    style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px", resize: "vertical" }}
                    onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #e2e8f0", padding: "16px 20px", background: "#f8fafc", borderRadius: "0 0 12px 12px" }}>
                <button type="button" className="btn" onClick={() => { setShowEditModal(false); setEditSheet(null); setCopyFieldsToOtherEntries(false); }} style={{ border: "1px solid #8b1634", color: "#8b1634", background: "transparent", fontWeight: 600, borderRadius: "8px", padding: "8px 20px" }}>Cancel</button>
                <button
                  type="button"
                  className="btn"
                  style={{ background: "#8b1634", color: "#fff", fontWeight: 600, borderRadius: "8px", padding: "8px 24px", border: "none", boxShadow: "0 2px 8px rgba(139,22,52,0.3)" }}
                  onClick={handleEditSave}
                  disabled={editModalLoading}
                >
                  {editModalLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FRI Schedule Modal (HSE Excel-style table) – large, centered */}
      {showFriModal && (
        <div
          className="modal fade show"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.4)",
            zIndex: 1050,
            padding: "1.5rem",
            boxSizing: "border-box",
          }}
          tabIndex={-1}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "min(1400px, 92vw)",
              maxHeight: "92vh",
              display: "flex",
              flexDirection: "column",
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", flexShrink: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h5 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600, color: "#1a202c" }}>
                  FRI Schedule {friData?.workSheet?.name ? `— ${friData.workSheet.name}` : ""} (Version 2.3)
                </h5>
                <button type="button" className="btn-close" onClick={closeFriModal} aria-label="Close" />
              </div>
            </div>
            <div style={{ padding: "1.25rem", overflow: "auto", flex: 1, minHeight: 0 }}>
              {friLoading ? (
                <div style={{ textAlign: "center", padding: "3rem" }}>
                  <div className="spinner-border text-danger" role="status" />
                  <p style={{ marginTop: "1rem", marginBottom: 0 }}>Loading FRI schedule...</p>
                </div>
              ) : friData && friData.workers && friData.workers.length > 0 ? (
                <>
                  {friData.workers.length > 1 && (
                    <div className="mb-3">
                      <label className="form-label">Worker</label>
                      <select
                        className="form-select"
                        style={{ maxWidth: "320px" }}
                        value={friSelectedWorkerIndex}
                        onChange={(e) => setFriSelectedWorkerIndex(Number(e.target.value))}
                      >
                        {friData.workers.map((w, i) => (
                          <option key={w.workerId} value={i}>
                            {w.workerName || w.workerEmail || w.workerId}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {currentFriWorker && (
                    <>
                      <div
                        className={`mb-3 p-3 rounded ${currentFriWorker.ruleResults?.passed ? "bg-success bg-opacity-10 border border-success" : "bg-danger bg-opacity-10 border border-danger"}`}
                      >
                        <strong>
                          {currentFriWorker.ruleResults?.passed
                            ? "Rules: Passed"
                            : `Rules: ${currentFriWorker.ruleResults?.level || "Breach"} — see details below`}
                        </strong>
                        {currentFriWorker.ruleResults?.breaches?.length > 0 && (
                          <ul className="mb-0 mt-2 ps-3">
                            {currentFriWorker.ruleResults.breaches.map((b, i) => (
                              <li key={i}>
                                <span className="badge bg-danger me-1">{b.level}</span> {b.detail}
                                {b.date && ` (${formatDate(b.date)})`}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="table-responsive" style={{ maxHeight: "58vh", overflow: "auto" }}>
                        <table className="table table-bordered table-sm" style={{ fontSize: "13px" }}>
                          <thead style={{ background: "#f7fafc", color: "#8b1634", position: "sticky", top: 0, zIndex: 1 }}>
                            <tr>
                              <th>Day</th>
                              <th>On Duty</th>
                              <th>Off Duty</th>
                              <th>Job type / breaks</th>
                              <th>Commuting Time</th>
                              <th>Duty Length</th>
                              <th>Average duty per day</th>
                              <th>Cumulative component</th>
                              <th>Duty timing component</th>
                              <th>Job type / Breaks component</th>
                              <th>Risk Index</th>
                            </tr>
                          </thead>
                          <tbody>
                            {friRows.length === 0 ? (
                              <tr>
                                <td colSpan={11} className="text-center text-muted py-4">
                                  No shift data in this period. Add shifts in Shift Entry.
                                </td>
                              </tr>
                            ) : (
                              friRows.map((row, i) => (
                                <tr key={row.shiftId || i}>
                                  <td>{row.day}</td>
                                  <td>{row.onDuty}</td>
                                  <td>{row.offDuty}</td>
                                  <td>{row.jobTypeBreaks}</td>
                                  <td>{row.commutingTime}</td>
                                  <td>{row.dutyLength}</td>
                                  <td>{row.averageDutyPerDay}</td>
                                  <td>{typeof row.cumulativeComponent === "number" ? row.cumulativeComponent.toFixed(2) : row.cumulativeComponent}</td>
                                  <td>{typeof row.dutyTimingComponent === "number" ? row.dutyTimingComponent.toFixed(2) : row.dutyTimingComponent}</td>
                                  <td>{typeof row.jobTypeBreaksComponent === "number" ? row.jobTypeBreaksComponent.toFixed(2) : row.jobTypeBreaksComponent}</td>
                                  <td style={{ fontWeight: row.riskIndex >= 1.0 ? "bold" : undefined }}>{typeof row.riskIndex === "number" ? row.riskIndex.toFixed(2) : row.riskIndex}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              ) : friData && (!friData.workers || friData.workers.length === 0) ? (
                <p className="text-muted mb-0">No workers or shift data for this work sheet.</p>
              ) : null}
            </div>
            <div style={{ padding: "1rem 1.25rem", borderTop: "1px solid #e2e8f0", background: "#f8fafc", flexShrink: 0 }}>
              <button type="button" className="btn btn-secondary" onClick={closeFriModal}>Close</button>
            </div>
          </div>
        </div>
      )}
      <EmergencyCallModal
        open={emergencyModalOpen}
        onClose={() => setEmergencyModalOpen(false)}
        defaultWorkerId={selectedUserId || ""}
        worksheetWorkers={worksheetWorkersToday}
      />
    </div>
  );
}

export default FatigueWorkSheetsPage;
